import React, { useState, useEffect, useCallback, useMemo, useRef, useTransition } from 'react';
import { createPortal } from 'react-dom';
import { Spinner, Alert, Card } from 'react-bootstrap';
import BookFullView from './components/BookFullView';
import CabinetPage from './components/CabinetPage';
import { GUEST_LOGIN, saveFulltextEntry, _storageFor, _qDedupeKey, getBookmarks, getFulltexts, fetchCabinetData, fetchOrderList, fetchReaderPhoto } from './components/CabinetPage';
import LoginPage from './components/UserLogin';
import LoginModal from './components/LoginModal';
import LangDropdown from './components/LangDropdown';
import AccessibilityPanel from './components/AccessibilityPanel';
import UdcPage from './components/UdcPage';
import JournalPage from './components/JournalPage';
import PaginationBlock from './components/PaginationBlock';
import CoverArt from './components/CoverArt';
import StatsPage from './components/StatsPage';
import {
  useSearchSelection, SelectCheckbox, SelectPageToggle, SelectionBar, SelectionModal, selectionStorageKey,
  SharedSelectionPage, selectionSharePath, parseSelectionShareUrl,
} from './components/SearchSelection';
import { API_URL } from './config';
import { onReaderSessionExpired, readerFetch } from './apiAuth';
import './Irbis.css';
import './components/SearchResults.css';
import { t } from './i18n';
// ── Трекінг статистики ────────────────────────────────────
function getOrCreateSessionId() {
  try {
    let sid = sessionStorage.getItem('irbis_stats_sid');
    if (!sid) {
      sid = 'sid_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem('irbis_stats_sid', sid);
    }
    return sid;
  } catch { return 'sid_unknown'; }
}

// Ідентифікатор читача (поле 30 RI= в RDR) для журналу дій — тільки для
// зареєстрованого читача; гість не має власного field 30.
function readerLoginOrNull(user) {
  return (user && user.mode === 'user' && user.login) ? user.login : null;
}

function trackEvent(eventType, params = {}) {
  try {
    const sid = getOrCreateSessionId();
    if (eventType === 'visit') {
      if (sessionStorage.getItem('irbis_stats_visited')) return;
      sessionStorage.setItem('irbis_stats_visited', '1');
    }
    fetch(`${API_URL}/stats.php?action=track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sid, event_type: eventType, ...params }),
    }).catch(() => {});
  } catch {}
}

// Окремий журнал текстів пошукових запитів (для адмінки — аналіз читацького
// інтересу, не плутати з trackEvent('search', ...) вище, який лише рахує
// категорії для агрегованої статистики).
function trackSearchLog(queryText, searchType, dbName, resultsCount, filters, login = null) {
  const hasQueryText = queryText && queryText.trim();
  if (!hasQueryText && !filters) return;
  try {
    fetch(`${API_URL}/stats.php?action=track_search_log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: hasQueryText ? queryText.trim() : '',
        search_type: searchType,
        db_name: dbName,
        filters: filters || null,
        results_count: resultsCount,
        login,
      }),
    }).catch(() => {});
  } catch {}
}

// Збирає тільки активні фільтри пошуку (для журналу — деталізація "як шукають",
// не тільки "що шукають"). Порожній об'єкт не передається (null замість {}).
function buildActiveFilters(q) {
  const f = {};
  if (q.searchType) f.mode = q.searchType;
  if (q.author?.trim()) f.author = q.author.trim();
  if (q.title?.trim()) f.title = q.title.trim();
  if (q.discipline?.trim()) f.discipline = q.discipline.trim();
  if (q.keywords?.trim()) f.keywords = q.keywords.trim();
  if (q.vydType) f.vydType = q.vydType;
  if (q.yearFrom) f.yearFrom = q.yearFrom;
  if (q.yearTo) f.yearTo = q.yearTo;
  if (q.hasFulltext) f.hasFulltext = true;
  if (q.hasExternalFulltext) f.hasExternalFulltext = true;
  if (q.langs?.length) f.langs = q.langs;
  return Object.keys(f).length ? f : null;
}

// ── Статичний список БД (фолбек, якщо API недоступний) ────
const DATABASES = [
  { value: 'KV',   label: 'Каталог видань'                      },
  { value: 'KST',  label: 'Картотека статей'                    },
  { value: 'KAD',  label: 'Каталог авторефератів та дисертацій' },
  { value: 'KSZRU', label: 'Каталог видань, переданих КЗРУ'     },
  { value: 'KWR',   label: 'Каталог корисних Інтернет-ресурсів'},
  { value: 'OF',    label: 'БД Обмінного фонду'                },
];


const getVydTypes = (lang) => [
  { value: '',    label: t(lang, 'vyd_all') },
  { value: 'KN',  label: t(lang, 'vyd_kn')  },
  { value: '05',  label: t(lang, 'vyd_05')  },
  { value: '03',  label: t(lang, 'vyd_03')  },
  { value: '04',  label: t(lang, 'vyd_04')  },
  { value: '01',  label: t(lang, 'vyd_01')  },
  { value: '02',  label: t(lang, 'vyd_02')  },
  { value: 'AS',  label: t(lang, 'vyd_as')  },
  { value: 'HD_m2', label: t(lang, 'vyd_hd_m2') },
  { value: 'HD_m1', label: t(lang, 'vyd_hd_m1') },
  { value: 'ze',  label: t(lang, 'vyd_ze')  },
  { value: 'zc',  label: t(lang, 'vyd_zc')  },
  { value: 'zk',  label: t(lang, 'vyd_zk')  },
  { value: 'zl',  label: t(lang, 'vyd_zl')  },
];
// Назви мов НЕ перекладаються через t() навмисно (крім української) —
// це власні назви мов, а не інтерфейсний текст
const getLangFilterOptions = (lang) => [
  { value: 'ukr', label: t(lang, 'lang_opt_ukr') },
  { value: 'eng', label: 'English' },
  { value: 'pol', label: 'Polish'  },
  { value: 'ger', label: 'German'  },
  { value: 'fre', label: 'French'  },
  { value: 'rus', label: 'Russian' },
];
const LANG_SHORT = { ukr: 'UK', eng: 'EN', pol: 'PL', ger: 'DE', fre: 'FR', rus: 'RU' };

// ── Закриття дропдауна кліком поза ним / клавішею Escape — однаковий
// патерн повторювався окремо в ControlDropdown і SortDropdown ──
function useCloseOnOutsideOrEscape(open, setOpen, ref) {
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen, ref]);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);
}

// ── Блокування скролу сторінки, поки на мобілі/планшеті відкрита
// «шторка» ControlDropdown/SortDropdown (.ctrl-dropdown-menu стає
// position:fixed bottom-sheet до 991.98px) — інакше палець на самій
// шторці скролить сторінку результатів позаду неї. На десктопі це
// звичайний, не на весь екран, дропдаун — блокувати скрол не треба. ──
function useLockBodyScrollOnMobileSheet(open) {
  useEffect(() => {
    if (!open) return;
    if (!window.matchMedia('(max-width: 991.98px)').matches) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);
}

// ── ControlDropdown ──────────────────────────────────────
function ControlDropdown({ value, onChange, options, label, block = false, lang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useCloseOnOutsideOrEscape(open, setOpen, ref);
  useLockBodyScrollOnMobileSheet(open);

  const selected = options.find(o => String(o.value) === String(value));

  return (
    <div
      ref={ref}
      className={`ctrl-dropdown${open ? ' ctrl-dropdown--open' : ''}${block ? ' ctrl-dropdown--block' : ''}`}
    >
      <button
        type="button"
        className="ctrl-dropdown-trigger"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
      >
        {selected?.icon && (
          <i className={`${selected.icon} ctrl-dropdown-item-icon`} aria-hidden="true" />
        )}
        <span className="ctrl-dropdown-label">{selected?.label ?? value}</span>
        <i className="fa-solid fa-chevron-down ctrl-dropdown-chevron" aria-hidden="true" />
      </button>

      <ul className="ctrl-dropdown-menu" role="listbox">
        <li className="ctrl-dropdown-close-row" role="presentation">
          <button type="button" className="ctrl-dropdown-close-btn" onClick={() => setOpen(false)}>
            <i className="fa-solid fa-chevron-down" aria-hidden="true" />
            {t(lang, 'sidebar_close')}
          </button>
        </li>
        {options.map(opt => {
          const isActive = String(opt.value) === String(value);
          return (
            <li
              key={opt.value}
              role="option"
              aria-selected={isActive}
              className={`ctrl-dropdown-item${isActive ? ' ctrl-dropdown-item--active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.icon && (
                <i className={`${opt.icon} ctrl-dropdown-item-icon`} aria-hidden="true" />
              )}
              <span>{opt.label}</span>
              {isActive && (
                <i className="fa-solid fa-check ctrl-dropdown-item-check" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>
      {open && (
  <div className="ctrl-dropdown-mobile-overlay" onClick={() => setOpen(false)} />
)}
    </div>
  );
}


// ── PaginationBlock — пагінація з номерами та переходом ──
// ── SortDropdown — з групами/заголовками секцій ───────────
function SortDropdown({ value, onChange, lang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useCloseOnOutsideOrEscape(open, setOpen, ref);
  useLockBodyScrollOnMobileSheet(open);

  const sortOptions = getSortOptions(lang);
  const selected = sortOptions.find(o => o.value === value);

  // Збираємо мітки груп для label кнопки: додаємо назву групи до пункту
  const selectedLabel = selected
    ? (selected.group ? `${selected.group}: ${selected.label}` : selected.label)
    : value;

  // Рендеримо список із заголовками груп
  const items = [];
  let lastGroup = undefined;
  sortOptions.forEach(opt => {
    if (opt.group !== lastGroup) {
      // Розділювач перед кожною новою секцією, крім самої першої
      if (lastGroup !== undefined) {
        items.push(<li key={`sep-${opt.group || 'end'}`} className="ctrl-dropdown-separator" role="presentation" />);
      }
      if (opt.group) {
        items.push(
          <li key={`hdr-${opt.group}`} className="ctrl-dropdown-group-header" role="presentation">
            {opt.group}
          </li>
        );
      }
      lastGroup = opt.group;
    }
    const isActive = opt.value === value;
    items.push(
      <li
        key={opt.value}
        role="option"
        aria-selected={isActive}
        className={`ctrl-dropdown-item${isActive ? ' ctrl-dropdown-item--active' : ''}`}
        onClick={() => { onChange(opt.value); setOpen(false); }}
      >
        {opt.icon && <i className={`${opt.icon} ctrl-dropdown-item-icon`} aria-hidden="true" />}
        <span>{opt.label}</span>
        {isActive && <i className="fa-solid fa-check ctrl-dropdown-item-check" aria-hidden="true" />}
      </li>
    );
  });

  return (
    <div ref={ref} className={`ctrl-dropdown${open ? ' ctrl-dropdown--open' : ''}`}>
      <button
        type="button"
        className="ctrl-dropdown-trigger"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t(lang, 'sort')}
      >
        {selected?.icon && <i className={`${selected.icon} ctrl-dropdown-item-icon`} aria-hidden="true" />}
        <span className="ctrl-dropdown-label">{selectedLabel}</span>
        <i className="fa-solid fa-chevron-down ctrl-dropdown-chevron" aria-hidden="true" />
      </button>
      <ul className="ctrl-dropdown-menu ctrl-dropdown-menu--grouped" role="listbox">
        <li className="ctrl-dropdown-close-row" role="presentation">
          <button type="button" className="ctrl-dropdown-close-btn" onClick={() => setOpen(false)}>
            <i className="fa-solid fa-chevron-down" aria-hidden="true" />
            {t(lang, 'sidebar_close')}
          </button>
        </li>
        {items}
      </ul>
      {open && <div className="ctrl-dropdown-mobile-overlay" onClick={() => setOpen(false)} />}
    </div>
  );
}

// Опції сортування: value, label, icon, group (для візуального поділу)
const getSortOptions = (lang) => [
  { value: 'relevance',   label: t(lang, 'sort_relevance'),    icon: 'fa-solid fa-arrow-down-wide-short', group: null },

  { value: 'year_desc',   label: t(lang, 'sort_newest_first'), icon: 'fa-solid fa-arrow-down-1-9', group: t(lang, 'sort_group_year') },
  { value: 'year_asc',    label: t(lang, 'sort_oldest_first'), icon: 'fa-solid fa-arrow-up-9-1',   group: t(lang, 'sort_group_year') },

  { value: 'date_desc',   label: t(lang, 'sort_newest_first'), icon: 'fa-solid fa-calendar-plus',  group: t(lang, 'sort_group_date') },
  { value: 'date_asc',    label: t(lang, 'sort_oldest_first'), icon: 'fa-solid fa-calendar-minus', group: t(lang, 'sort_group_date') },

  { value: 'title_asc',   label: t(lang, 'sort_az'), icon: 'fa-solid fa-arrow-down-a-z', group: t(lang, 'sort_group_title') },
  { value: 'title_desc',  label: t(lang, 'sort_za'), icon: 'fa-solid fa-arrow-up-z-a',   group: t(lang, 'sort_group_title') },

  { value: 'author_asc',  label: t(lang, 'sort_az'), icon: 'fa-solid fa-user', group: t(lang, 'sort_group_author') },
  { value: 'author_desc', label: t(lang, 'sort_za'), icon: 'fa-solid fa-user', group: t(lang, 'sort_group_author') },

  { value: 'type_asc',    label: t(lang, 'sort_az'), icon: 'fa-solid fa-file-lines', group: t(lang, 'sort_group_doctype') },
];

const getPerPageOptions = (lang) => [
  { value: 10,    label: `10 ${t(lang, 'perpage_suffix')}` },
  { value: 20,    label: `20 ${t(lang, 'perpage_suffix')}` },
  { value: 40,    label: `40 ${t(lang, 'perpage_suffix')}` },
  { value: 60,    label: `60 ${t(lang, 'perpage_suffix')}` },
  { value: 'all', label: t(lang, 'perpage_all') },
];
 const SEARCH_TIMEOUT_MS = 45000;
// ══════════════════════════════════════════════════════════
// ХУК: динамічний список баз даних з ІРБІС
// Фолбек на статичний DATABASES, якщо API недоступний.
// ══════════════════════════════════════════════════════════
function useDatabases() {
  const [crossDbDatabases, setCrossDbDatabases] = useState(DATABASES);

  useEffect(() => {
    fetch(`${API_URL}/databases.php?action=list`)
      .then(r => r.json())
      .then(d => {
        // Вайтліст для cross-db панелі (єдиний споживач списку баз —
        // головний вибір бази прибрано з UI)
        if (Array.isArray(d.crossdb_databases) && d.crossdb_databases.length > 0) {
          setCrossDbDatabases(d.crossdb_databases.map(db => ({ value: db.value, label: db.label })));
        } else if (Array.isArray(d.databases) && d.databases.length > 0) {
          setCrossDbDatabases(d.databases.map(db => ({ value: db.value, label: db.label })));
        }
      })
      .catch(() => {});
  }, []);

  return { crossDbDatabases };
}

// ── URL маршрутизація і заголовки вкладки ────────────────
const SITE_NAME = 'Електронний каталог та електронна бібліотека';

// ── Формат імені: "Прізвище І.О." ────────────────────────
function formatInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const surname  = parts[0];
  const initials = parts.slice(1)
    .map(p => (p[0] || '').toUpperCase() + '.')
    .join('');
  return `${surname} ${initials}`;
}

// Заглушка обкладинки: контейнер (розмір/показ — як і раніше) + векторна обкладинка за типом видання
function CoverPlaceholder({ cls, hidden, type, db }) {
  return (
    <div className={`${cls} cover-art-host`} style={{ display: hidden ? 'none' : 'flex' }}>
      <CoverArt type={type} db={db} />
    </div>
  );
}

// Ініціали для аватара (до 2 літер). Однаковий код повторювався в навбарі
// та в боковій панелі користувача — це не formatInitials (той дає
// «Прізвище І.» для підпису імені, тут потрібні дві літери в кружечку).
function avatarInitials(name) {
  return (name || '?').split(' ').filter(Boolean).slice(0, 2)
    .map(w => w[0].toUpperCase()).join('');
}

// onError для <img> обкладинки: ховаємо картинку, показуємо сусідню
// заглушку (nextSibling може бути текстовим вузлом — пропускаємо його).
// Однаковий код повторювався в списку та в сітці результатів пошуку.
function coverImgError(e) {
  e.target.style.display = 'none';
  let sib = e.target.nextSibling;
  while (sib && sib.nodeType !== 1) sib = sib.nextSibling;
  if (sib) sib.style.display = 'flex';
}

// Слова останнього виконаного пошукового запиту — для підсвітки в картках
// результатів (сесія 2026-09-11). Порожні/суто пунктуаційні токени
// відкидаються тим самим паттерном, що вже використовує search.php.
function tokenizeHighlightWords(text) {
  if (!text) return [];
  const seen = new Set();
  text.trim().split(/\s+/u).forEach(w => {
    if (/[\p{L}\p{N}]/u.test(w)) seen.add(w.toLocaleLowerCase());
  });
  return Array.from(seen);
}

// Підсвічує в тексті ті слова, що є ПРЕФІКСОМ якогось слова запиту —
// відповідає реальній семантиці пошуку ІРБІС (K=слово$ — усічення,
// шукає "починається на"), а не довільну підрядок-відповідність.
function highlightText(text, words) {
  if (!text || !words || words.length === 0) return text;
  const segments = String(text).split(/([\p{L}\p{N}]+)/u);
  return segments.map((seg, i) => {
    if (!seg) return null;
    if (/[\p{L}\p{N}]/u.test(seg) && words.some(w => seg.toLocaleLowerCase().startsWith(w))) {
      return <mark key={i} className="result-highlight">{seg}</mark>;
    }
    return seg;
  });
}

// Мапінг backend-коду matched_hint (search.php) → ключ i18n назви поля.
const HINT_LABEL_KEYS = {
  keywords:   'result_hint_keywords',
  subjects:   'result_hint_subjects',
  contents:   'result_hint_contents',
  annotation: 'result_hint_annotation',
};

// Бейджі під назвою картки результату: рік / «Журнал» / повний текст / мови.
// У сітці кожен бейдж має mt-1, а бейдж повного тексту винесений в
// обкладинку (showFulltext=false); у списку — без mt-1, повний текст тут.
function ResultMetaBadges({ book, lang, mt, showFulltext }) {
  const m = mt ? ' mt-1' : '';
  return (
    <>
      {book.year && (
        <span className={'result-meta-year' + m}>
          <i className="fa-regular fa-calendar" />{book.year}
        </span>
      )}
      {book.is_journal && (
        <span className={'result-type-badge' + m} title={t(lang, 'result_journal_title')}>
          <i className="fa-solid fa-book-open me-1" />{t(lang, 'result_journal_badge')}
        </span>
      )}
      {showFulltext && book.has_fulltext && (
        <span className={'result-fulltext-badge' + m} title={t(lang, 'fulltext_available_title')}>
          <i className="fa-solid fa-file-lines me-1" />{t(lang, 'fulltext_available_badge')}
        </span>
      )}
      {book.all_langs && book.all_langs.length > 0 && (
        <span className={'result-lang-badge' + m} title={t(lang, 'result_lang_title')}>
          <i className="fa-solid fa-language" style={{ marginRight: 4, opacity: 0.7 }} />
          {book.all_langs.slice(0, 3).map(l => LANG_SHORT[l] || l.slice(0, 3).toUpperCase()).join(' · ')}
        </span>
      )}
      {book.matched_hint && HINT_LABEL_KEYS[book.matched_hint] && (
        <span className={'result-hint-badge' + m} title={t(lang, 'result_hint_title')}>
          <i className="fa-solid fa-magnifying-glass-plus me-1" />
          {t(lang, 'result_hint_prefix')} {t(lang, HINT_LABEL_KEYS[book.matched_hint])}
        </span>
      )}
    </>
  );
}

const getCabinetPathMap = (lang) => ({
  issued:    { path: 'mybooks',     title: t(lang, 'cab_issued')    },
  history:   { path: 'history',     title: t(lang, 'cab_history')   },
  visits:    { path: 'visits',      title: t(lang, 'cab_visits')    },
  fulltexts: { path: 'fulltexts',   title: t(lang, 'cab_fulltexts') },
  bookmarks: { path: 'mybookmarks', title: t(lang, 'cab_bookmarks') },
  orders:    { path: 'myorders',    title: t(lang, 'cab_orders')    },
  queries:   { path: 'myrequests',  title: t(lang, 'cab_queries')   },
});
function slugify(str) {
  if (!str) return '';
  const cyr = {
    'а':'a','б':'b','в':'v','г':'h','ґ':'g','д':'d','е':'e','є':'ie',
    'ж':'zh','з':'z','и':'y','і':'i','ї':'i','й':'i','к':'k','л':'l',
    'м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
    'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch','ь':'',
    'ю':'iu','я':'ia','ё':'yo','э':'e','ъ':'','ы':'y',
  };
  return str
    .toLowerCase()
    .replace(/[\u0400-\u04FF]/g, c => cyr[c] ?? '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function parseBookUrl(pathname) {
  const m = pathname.match(/\/book\/([a-zA-Z]+)\/(\d+)/);
  if (!m) return null;
  return { db: m[1].toUpperCase(), mfn: parseInt(m[2], 10) };
}
function parseJournalUrl(pathname) {
  const m = pathname.match(/\/journal\/([a-zA-Z]+)\/([^/]+)/);
  if (!m) return null;
  // decodeURIComponent кидає URIError на битому %-кодуванні у посиланні —
  // без try/catch це ронить увесь CatalogPage ще на монтуванні (виклик
  // стоїть в ініціалізаторах useState). Фолбек — сирий сегмент URL.
  let code;
  try { code = decodeURIComponent(m[2]); } catch { code = m[2]; }
  return { db: m[1].toUpperCase(), code };
}
// Спільна формула тексту «Повернутися до…» для udc/stats/journal —
// заміняє продубльовану тернарну гілку, яка раніше окремо жила
// у onOpenUdc і onOpenStats (аудит дублювання, сесія 2026-08-05).
function computeReturnLabel(lang, { selectedBook, activeSection, total }) {
  if (selectedBook) return t(lang, 'back_book');
  if (activeSection === 'cabinet') return t(lang, 'back_cabinet');
  if (activeSection === 'udc') return t(lang, 'back_udc');
  if (activeSection === 'journal') return t(lang, 'back_journal');
  if (total !== null) return t(lang, 'back_results');
  return t(lang, 'back_home');
}
function setPageRoute(path, title, extra = {}, url) {
  document.title = `${title} | ${SITE_NAME}`;
  window.history.pushState({ path, ...extra }, '', url ?? `/${path}`);
}

// ── ScrollToTop ───────────────────────────────────────────
function ScrollToTop({ lang }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handler = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  if (!visible) return null;
  return createPortal(
    <button
      className="scroll-to-top-btn"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label={t(lang, 'scroll_top_aria')}>
      <i className="fa-solid fa-arrow-up" />
    </button>,
    document.body
  );
}

// ── Ресурси бібліотеки (спільний список — картка на головній + подвал) ──
const RESOURCE_LINKS = [
  { href: 'https://lib.udu.edu.ua',         icon: 'fa-solid fa-building',    labelKey: 'home_res_library' },
  { href: 'https://enpuir.udu.edu.ua/home',  icon: 'fa-solid fa-box-archive', labelKey: 'home_res_repo'    },
  { href: 'https://libdc.udu.edu.ua/',       icon: 'fa-solid fa-photo-film',  labelKey: 'home_res_digital' },
];

// ── Footer ────────────────────────────────────────────────
function AppFooter({ onOpenStats, lang }) {
  return (
    <footer className="app-footer">
      <div className="app-footer-inner">
        <button className="app-footer-stats-btn" onClick={onOpenStats}>
          <i className="fa-solid fa-chart-bar" />
          {t(lang, 'stats_nav')}
        </button>
        <div className="app-footer-info">
          <div className="app-footer-line">
            <span className="app-footer-copyright">{t(lang, 'lp_copyright')}</span>
            <span className="app-footer-line-sep" aria-hidden="true">•</span>
            <span className="app-footer-made">
              {t(lang, 'footer_made_with')}{' '}
              <a href="https://react.dev" target="_blank" rel="noopener noreferrer"
                className="app-footer-tech-link app-footer-tech-link--react">
                <i className="fa-brands fa-react app-footer-tech-icon" />
                React JS
              </a>
              {' '}{t(lang, 'footer_and')}{' '}
              <a href="https://www.php.net" target="_blank" rel="noopener noreferrer"
                className="app-footer-tech-link app-footer-tech-link--php">
                <i className="fa-brands fa-php app-footer-tech-icon" />
                PHP
              </a>
            </span>
          </div>
          <div className="app-footer-author">
            <span className="app-footer-author-pill">
              <span className="app-footer-author-label">{t(lang, 'footer_author')}</span>
              <a href="mailto:a.o.mariienko@udu.edu.ua" className="app-footer-author-link">
                a.o.mariienko@udu.edu.ua
              </a>
            </span>
          </div>
          <div className="app-footer-resources">
            <div className="app-footer-resources-list">
              {RESOURCE_LINKS.map(r => (
                <a key={r.href} href={r.href} target="_blank" rel="noreferrer"
                  className="app-footer-resource-link">
                  <i className={`${r.icon} app-footer-resource-icon`} />
                  {t(lang, r.labelKey)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── saveQuery — зберігає пошуковий запит ─────────────────
// _storageFor/_qDedupeKey — спільні з CabinetPage.js (там же і дедуплікуються
// збережені запити при показі — важливо рахувати ключ однаково в обох місцях).
function saveQuery(user, queryObj) {
  if (!user) return;
  const isGuest = user.mode === 'guest';
  const login   = isGuest ? GUEST_LOGIN : user.login;
  const key     = `irbis_queries_${login}`;
  if (!isGuest && !user.login) return;

  const qKey = _qDedupeKey(queryObj);
  try {
    const existing = JSON.parse(_storageFor(login).getItem(key) || '[]');
    const deduped = existing.filter(q => _qDedupeKey(q) !== qKey);
    _storageFor(login).setItem(key, JSON.stringify([queryObj, ...deduped].slice(0, 50)));
  } catch {}

  // MySQL — довготривале зберігання для зареєстрованого читача (сесія 2026-08-12,
  // queries.php). Локальний кеш вище лишається — на ньому тримається синхронний
  // дропдаун "історія" під полем пошуку (refreshSuggestions нижче).
  if (!isGuest && user.reader_token) {
    readerFetch(`${API_URL}/queries.php?action=save`, user.reader_token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(queryObj),
    }).catch(() => {});
  }
}


// ═══════════════════════════════════════════════════════════
// SEARCH QUERY AUTOCOMPLETE
// Показує дві секції:
//   1) Живі підказки з ІРБІС (suggest.php, debounce 300ms)
//   2) Останні запити (localStorage / sessionStorage)
// ═══════════════════════════════════════════════════════════
function SearchQueryAutocomplete({ user, value, onChange, onSubmit, onSelectAndSearch, onSelectLiveSuggestion, inputProps, db, lang }) {
  const [open,            setOpen]            = useState(false);
  const [suggestions,     setSuggestions]     = useState([]);
  const [liveSuggestions, setLiveSuggestions] = useState([]);
  const [liveLoading,     setLiveLoading]     = useState(false);
  const [activeIdx,       setActiveIdx]       = useState(-1);
  const containerRef = useRef(null);
  const abortRef     = useRef(null);

  // ── Історія запитів із сховища ─────────────────────────
  const refreshSuggestions = useCallback(() => {
    const isGuest = user?.mode === 'guest';
    const login   = isGuest ? GUEST_LOGIN : user?.login;
    if (!login) { setSuggestions([]); return; }
    const key = `irbis_queries_${login}`;
    try {
      const raw = isGuest
        ? JSON.parse(sessionStorage.getItem(key) || '[]')
        : JSON.parse(localStorage.getItem(key)   || '[]');
      const q       = value.trim().toLowerCase();
      const filtered = raw
        .filter(r => r.query && (!q || r.query.toLowerCase().includes(q)))
        .filter((r, i, arr) => arr.findIndex(x => x.query === r.query) === i)
        .slice(0, 6);
      setSuggestions(filtered);
    } catch { setSuggestions([]); }
  }, [value, user]);

  useEffect(() => { refreshSuggestions(); }, [refreshSuggestions]);

// ── Живі підказки з ІРБІС (readTerms — швидкий словник) ──
useEffect(() => {
  if (abortRef.current) abortRef.current.abort();

  const trimmed = value.trim();
  if (trimmed.length < 2) {
    setLiveSuggestions([]);
    setLiveLoading(false);
    return;
  }

  const controller = new AbortController();
  abortRef.current = controller;
  setLiveLoading(true);

  const timer = setTimeout(async () => {
    try {
      const url = `${API_URL}/suggest.php?q=${encodeURIComponent(trimmed)}&db=${encodeURIComponent(db)}&limit=7`;
      const res  = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error('suggest error');
      const data = await res.json();
      if (!controller.signal.aborted) setLiveSuggestions(data.suggestions || []);
    } catch (err) {
      if (err.name !== 'AbortError') setLiveSuggestions([]);
    } finally {
      if (!controller.signal.aborted) setLiveLoading(false);
    }
  }, 180); // 180 мс — баланс між швидкістю і кількістю запитів

  return () => { clearTimeout(timer); controller.abort(); };
// eslint-disable-next-line react-hooks/exhaustive-deps
},[value, db]);

// ── Закриття по кліку назовні ──────────────────────────
  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // ── Видалення одного запиту з історії ─────────────────
  const deleteQuery = (e, queryToDelete) => {
    e.stopPropagation();
    e.preventDefault();
    const isGuest = user?.mode === 'guest';
    const login = isGuest ? GUEST_LOGIN : user?.login;
    if (!login) return;
    const key = `irbis_queries_${login}`;
    // Дропдаун показує один рядок на текст запиту, тому видаляємо ВСІ варіанти
    // з цим текстом (з різними фільтрами / «Шукати у знайденому») — і локально,
    // і в MySQL. Раніше з MySQL видалявся лише перший, решта лишались у «Мої запити».
    let removedObjs = [];
    try {
      const items = JSON.parse(_storageFor(login).getItem(key) || '[]');
      removedObjs = items.filter(r => r.query === queryToDelete);
      _storageFor(login).setItem(key, JSON.stringify(items.filter(r => r.query !== queryToDelete)));
    } catch {}
    setSuggestions(prev => prev.filter(s => s.query !== queryToDelete));

    if (!isGuest && user?.reader_token) {
      removedObjs.forEach(removedObj => {
        readerFetch(`${API_URL}/queries.php?action=remove`, user.reader_token, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: removedObj.query, author: removedObj.author, title: removedObj.title,
            discipline: removedObj.discipline, keywords: removedObj.keywords, db: removedObj.db,
            yearFrom: removedObj.yearFrom, searchType: removedObj.searchType,
          }),
        }).catch(() => {});
      });
    }
  };

  // ── Очистити всю історію ───────────────────────────────
  const clearAll = e => {
    e.stopPropagation();
    e.preventDefault();
    const isGuest = user?.mode === 'guest';
    const login = isGuest ? GUEST_LOGIN : user?.login;
    if (!login) return;
    _storageFor(login).removeItem(`irbis_queries_${login}`);
    setSuggestions([]);
    setOpen(false);

    if (!isGuest && user?.reader_token) {
      readerFetch(`${API_URL}/queries.php?action=clear`, user.reader_token, { method: 'POST' }).catch(() => {});
    }
  };

  // ── Вибір підказки з історії ──────────────────────────
  const selectSuggestion = q => {
    onChange(q);
    setOpen(false);
    setActiveIdx(-1);
    onSelectAndSearch?.(q);
  };

  // ── Вибір живої підказки (назва з ІРБІС) ──────────────
  // НЕ замінюємо value на назву книги — зберігаємо те, що ввів користувач.
  // Збереження запиту в історію відбувається у AppNavbar.selectLiveSuggestion.
  const selectLive = (title, mfn, suggDb) => {
    setOpen(false);
    setActiveIdx(-1);
    onSelectLiveSuggestion?.(title, mfn, suggDb);
  };

  // ── Клавіатурна навігація ─────────────────────────────
  // Індексація: [0..liveSuggestions.length-1] = live,
  //             [liveSuggestions.length..] = history
  const allItems = [
    ...liveSuggestions.map(s => ({ label: s.title, type: 'live',    data: s })),
    ...suggestions.map(s     => ({ label: s.query,  type: 'history', data: s })),
  ];

  const handleKeyDown = e => {
    if (!open || !allItems.length) { if (e.key === 'Enter') onSubmit(); return; }
    if      (e.key === 'ArrowDown')  { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, allItems.length - 1)); }
    else if (e.key === 'ArrowUp')    { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter') {
      if (activeIdx >= 0) {
        const item = allItems[activeIdx];
        if (item.type === 'live') selectLive(item.label, item.data.mfn, db);
        else                      selectSuggestion(item.label);
      } else { onSubmit(); setOpen(false); }
    }
    else if (e.key === 'Escape') { setOpen(false); }
  };

const hasLive    = liveLoading || liveSuggestions.length > 0;
  const hasHistory = suggestions.length > 0;
  const showList   = open && (hasLive || hasHistory);
  // Порожній стан: дропдаун відкритий, тексту достатньо (як і для живого пошуку),
  // але ні живих підказок, ні історії немає — типово після зміни бази даних,
  // де для цього тексту просто немає збігів.
  const showEmpty  = open && !hasLive && !hasHistory && value.trim().length >= 2;

  return (
    <div
      ref={containerRef}
      className="search-autocomplete-wrap"
      role="combobox"
      aria-expanded={showList || showEmpty}
      aria-haspopup="listbox"
      aria-controls="search-autocomplete-listbox"
      aria-owns="search-autocomplete-listbox"
    >
      <input
        {...inputProps}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
      />

      {showList && (
        <ul className="search-autocomplete-list" id="search-autocomplete-listbox" role="listbox">

          {/* ── Секція: живі підказки ІРБІС ── */}
          {hasLive && (
            <>
              {liveSuggestions.map((s, i) => {
                const globalIdx = i;
                return (
                  <li
                    key={`live-${i}`}
                    role="option"
                    aria-selected={globalIdx === activeIdx}
                    className={`search-autocomplete-item search-autocomplete-item--live${globalIdx === activeIdx ? ' search-autocomplete-item--active' : ''}`}
                    onMouseDown={e => { e.preventDefault(); selectLive(s.title, s.mfn, db); }}
                  >
                    <i className="fa-solid fa-magnifying-glass search-autocomplete-icon search-autocomplete-icon--live" />
                    <span className="search-autocomplete-text">{s.title}</span>
                    {s.year && <span className="search-autocomplete-date">{s.year}</span>}
                  </li>
                );
              })}
              {liveLoading && liveSuggestions.length === 0 && (
                <li className="search-autocomplete-loading" role="presentation">
                  <span className="search-autocomplete-spinner-dot" />
                  <span className="search-autocomplete-spinner-dot" />
                  <span className="search-autocomplete-spinner-dot" />
                </li>
              )}
            </>
          )}

          {/* ── Секція: останні запити ── */}
          {hasHistory && (
            <>
              {/* Розділювач між живими підказками та історією */}
              {hasLive && liveSuggestions.length > 0 && (
                <li className="search-autocomplete-divider" role="separator" aria-hidden="true" />
              )}
              <li className="search-autocomplete-header" role="presentation">
                <span className="search-autocomplete-header-label">
                  <i className="fa-solid fa-clock-rotate-left me-1" />{t(lang, 'search_recent_queries')}
                </span>
                <button
                  className="search-autocomplete-clear-all"
                  onMouseDown={clearAll}
                  title={t(lang, 'udc_history_clear_all_title')}
                >
                  {t(lang, 'udc_history_clear_all')}
                </button>
              </li>
              {suggestions.map((s, i) => {
                const globalIdx = liveSuggestions.length + i;
                return (
                  <li
                    key={`hist-${i}`}
                    role="option"
                    aria-selected={globalIdx === activeIdx}
                    className={`search-autocomplete-item${globalIdx === activeIdx ? ' search-autocomplete-item--active' : ''}`}
                    onMouseDown={e => { e.preventDefault(); selectSuggestion(s.query); }}
                  >
                    <i className="fa-solid fa-clock-rotate-left search-autocomplete-icon" />
                    <span className="search-autocomplete-text">{s.query}</span>
                    {s.date && <span className="search-autocomplete-date">{s.date}</span>}
                    <button
                      className="search-autocomplete-delete"
                      onMouseDown={e => deleteQuery(e, s.query)}
                      title={t(lang, 'query_remove_title')}
                      aria-label={`${t(lang, 'query_remove_aria_prefix')} «${s.query}»`}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </li>
                );
              })}
            </>
          )}

        </ul>
      )}

      {showEmpty && (
        <ul className="search-autocomplete-list search-autocomplete-list--empty" id="search-autocomplete-listbox" role="listbox">
          <li className="search-autocomplete-empty" role="presentation">
            <i className="fa-solid fa-magnifying-glass-minus" aria-hidden="true" />
            <span>{t(lang, 'search_no_suggestions')}</span>
          </li>
        </ul>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// APP NAVBAR
// ═══════════════════════════════════════════════════════════
function AppNavbar({
  user, onLogout,
  query, setQuery, db,
  onSearch, onGoHome,
  showMobileSearch, onToggleMobileSearch,
  onToggleUserPanel, userPanelOpen,
  darkMode, onToggleDark,
  onOpenBook,
  lang, onLangChange,
  selectedLangs,
  hasFulltext,
  hasExternalFulltext,
  searchType, author, title, discipline, keywords, vydType, yearFrom, yearTo,
  onToggleSidebar, sidebarOpen, activeFilterCount,
  showHomeTitle,
}) {
  const submit = () => {
    if (query.trim() || author?.trim() || title?.trim() || discipline?.trim()
        || keywords?.trim()
        || vydType || yearFrom || yearTo
        || selectedLangs.length > 0 || hasFulltext || hasExternalFulltext) onSearch(1);
  };

// Обов'язково включає ВСІ активні фільтри бокової панелі (не лише langs/hasFulltext,
// як було до сесії 2026-08-07/3) — інакше вибір збереженого запиту чи підказки з
// історії/автопідказок мовчки скидає «Вид/тип видання»/автора/назву/роки, хоча
// сам стан фільтра (і бейдж лічильника) в UI лишається незмінним — знайдено на
// прикладі фільтра «Журнал» + пошук за збереженим запитом
const submitQuery = q => {
    if (!q?.trim()) return;
    setQuery(q);
    onSearch(1, {
      query: q, db,
      searchType: searchType === 'UDC' ? 'K_ANY' : searchType,
      author, title, discipline, keywords,
      vydType, yearFrom, yearTo, hasFulltext, hasExternalFulltext,
      langs: selectedLangs,
    });
  };

  // При виборі живої підказки — відкриваємо книгу напряму по MFN.
  // Важливо:
  //   1) Зберігаємо ПОТОЧНИЙ частковий запит користувача (наприклад, "текст")
  //      в історію — не назву книги.
  //   2) НЕ змінюємо поле пошуку: після переходу до картки і назад
  //      користувач бачить свій оригінальний запит.
  //   Якщо MFN відсутній — fallback: пошук усіх слів заголовку (KT_ALL).
  const selectLiveSuggestion = (title, mfn, suggDb) => {
    const currentQuery = query.trim();

    // Зберігаємо в історію те, що фактично ввів користувач
    if (currentQuery) {
      saveQuery(user, {
        query:  currentQuery,
        author: '',
        title:  '',
        db:     suggDb || db,
        date:   new Date().toLocaleDateString('uk-UA'),
      });
    }

    if (mfn > 0 && onOpenBook) {
      onOpenBook(mfn, suggDb || db, title, 'catalog');
    } else {
  // fallback: точний збіг за повною назвою з підказки
  setQuery(title);
  onSearch(1, {
    query:      title,
    db:         suggDb || db,
    searchType: 'KT_PHRASE',
    author: '', title: '', discipline: '', keywords: '',
    vydType: '', yearFrom: '', yearTo: '',
    hasFulltext: false,
  });
}
  };
// Full-screen мобільний пошук: блокуємо скрол сторінки, поки він відкритий;
  // Escape закриває — узгоджено з поведінкою інших дропдаунів у проєкті.
  useEffect(() => {
    if (!showMobileSearch) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = e => { if (e.key === 'Escape') onToggleMobileSearch(); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showMobileSearch, onToggleMobileSearch]);

  return (
    <header className={`app-navbar${showHomeTitle ? ' app-navbar--home' : ''}`}>
      {/* Заголовок — тільки на головній сторінці каталогу; окремий рядок на всю ширину шапки */}
      {showHomeTitle && (
        <h1 className="app-navbar-home-title">{t(lang, 'home_main_title')}</h1>
      )}
<div className="app-navbar-brand-wrap">
<button className="app-navbar-brand" onClick={onGoHome} aria-label={t(lang, 'nav_home')}>
  {/* Логотип — только мобильные; иконка — только десктоп */}
  <img
  src={`${process.env.PUBLIC_URL}/images/liblogo.png`}
  alt={t(lang, 'logo_alt')}
  className="app-navbar-brand-logo"
  onError={e => { e.target.style.display = 'none'; }}
/>
</button>
</div>

      {/* Кнопка «Опції пошуку» — тільки десктоп */}
      <button
        className={`app-navbar-options-btn d-none d-lg-flex${sidebarOpen ? ' active' : ''}`}
        onClick={onToggleSidebar}
        aria-label={t(lang, 'sidebar_options_btn')}
        aria-expanded={sidebarOpen}
      >
        <i className="fa-solid fa-sliders" />
        <span>{t(lang, 'sidebar_options_btn')}</span>
        {activeFilterCount > 0 && <span className="navbar-filter-badge">{activeFilterCount}</span>}
      </button>

      {/* Центральний пошук — десктоп */}
      <div className="app-navbar-search d-none d-lg-flex" role="search">
        <div className="app-navbar-input-wrap" style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
          <SearchQueryAutocomplete
            user={user}
            value={query}
            onChange={setQuery}
            onSubmit={submit}
            onSelectAndSearch={submitQuery}
            onSelectLiveSuggestion={selectLiveSuggestion}
            db={db}
            lang={lang}
            inputProps={{
              type: 'search',
              className: 'app-navbar-input',
              placeholder: t(lang, 'search_placeholder'),
              'aria-label': t(lang, 'search_query_aria'),
            }}
          />
          {query && (
            <button className="app-navbar-clear" onClick={() => setQuery('')} aria-label={t(lang, 'udc_clear_input')}>
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>
        <button className="app-navbar-search-btn" onClick={submit} aria-label={t(lang, 'search_btn')}>
          <i className="fa-solid fa-magnifying-glass" />
        </button>
      </div>

      {/* Правий блок */}
      <div className="app-navbar-right">
        <button
          className={`app-navbar-icon-btn d-flex d-lg-none${showMobileSearch ? ' active' : ''}`}
          onClick={onToggleMobileSearch}
          aria-label={t(lang, 'search_aria')}
          aria-expanded={showMobileSearch}>
          <i className={showMobileSearch ? 'fa-solid fa-xmark' : 'fa-solid fa-magnifying-glass'} />
        </button>
<button
  className="app-navbar-icon-btn d-flex d-md-none"
  onClick={() => onLangChange(lang === 'uk' ? 'en' : 'uk')}
  aria-label={t(lang, 'lang_switch_aria')}
  title={t(lang, 'lang_switch_title')}>
  <i className="fa-solid fa-globe" />
</button>
<div className="d-none d-md-block">
  <LangDropdown lang={lang} onChange={onLangChange} />
</div>
        <button
          className="app-navbar-icon-btn app-navbar-theme-toggle"
          onClick={onToggleDark}
          aria-label={darkMode ? t(lang, 'theme_light') : t(lang, 'theme_dark')}
          title={darkMode ? t(lang, 'theme_to_light') : t(lang, 'theme_to_dark')}>
          <i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />
        </button>
<div className={`navbar-user-block${userPanelOpen ? ' active' : ''}`}>
<button type="button"
  className={`navbar-user-avatar${user.mode === 'guest' ? ' navbar-user-avatar--guest' : ''}`}
  onClick={onToggleUserPanel}
  title={user.mode === 'guest' ? t(lang, 'guest_label') : user.name}
  tabIndex={-1} aria-hidden="true">
{user.mode === 'guest'
  ? <i className="fa-solid fa-user" />
  : <span>{avatarInitials(user.name || user.login)}</span>}
</button>
          <button
            className={`app-navbar-user-btn app-navbar-hamburger${userPanelOpen ? ' active' : ''}`}
            onClick={onToggleUserPanel}
            title={user.mode === 'guest' ? t(lang, 'guest_panel_title') : user.name}
            aria-label={t(lang, 'user_menu_aria')}
            aria-expanded={userPanelOpen}
          >
            <i className={`fa-solid ${userPanelOpen ? 'fa-xmark' : 'fa-bars'}`} />
          </button>
        </div>
        <button className="app-navbar-logout-btn" onClick={onLogout} aria-label={t(lang, 'logout')}>
          <i className="fa-solid fa-right-from-bracket" />
          <span className="d-none d-sm-inline ms-1">{t(lang, 'logout')}</span>
        </button>
      </div>

{/* Повноекранний мобільний пошук (Google-подібний режим) */}
      {showMobileSearch && createPortal(
        <div className="mobile-search-overlay d-flex d-lg-none" role="search">
          <div className="mobile-search-overlay-topbar">
            <div className="mobile-search-overlay-row1">
              <button
                className={`mobile-search-filter-btn${sidebarOpen ? ' active' : ''}`}
                onClick={onToggleSidebar}
                aria-label={t(lang, 'sidebar_options_btn')}
                aria-expanded={sidebarOpen}>
                <i className="fa-solid fa-sliders" />
                {activeFilterCount > 0 && <span className="navbar-filter-badge">{activeFilterCount}</span>}
              </button>
              <button
                className="mobile-search-back-btn"
                onClick={onToggleMobileSearch}
                aria-label={t(lang, 'search_close_aria')}>
                <i className="fa-solid fa-arrow-left" />
              </button>
            </div>
            <div className="mobile-search-overlay-row2">
              <div className="app-navbar-input-wrap flex-grow-1" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <SearchQueryAutocomplete
                user={user}
                value={query}
                onChange={setQuery}
                onSubmit={submit}
                onSelectAndSearch={submitQuery}
                onSelectLiveSuggestion={selectLiveSuggestion}
                db={db}
                lang={lang}
                inputProps={{
                  type: 'search',
                  className: 'app-navbar-input',
                  placeholder: t(lang, 'search_placeholder_short'),
                  autoFocus: true,
                }}
              />
              {query && (
                <button className="app-navbar-clear" onClick={() => setQuery('')}>
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
                <button className="app-navbar-search-btn" onClick={submit}>
                  <i className="fa-solid fa-magnifying-glass" />
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}

// ═══════════════════════════════════════════════════════════
// BREADCRUMBS
// ═══════════════════════════════════════════════════════════
function Breadcrumbs({ items }) {
  if (!items || items.length <= 1) return null;
  return (
    <nav className="app-breadcrumbs" aria-label="breadcrumb">
      <ol className="breadcrumb-list">
        {items.map((item, i) => (
          <li key={i} className={`app-breadcrumb-item${item.current ? ' app-breadcrumb-item--current' : ''}`}>
            {i > 0 && (
              <span className="breadcrumb-sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>
            )}
            {item.current
              ? <span className="breadcrumb-current">{item.label}</span>
              : <button className="breadcrumb-link" onClick={item.onClick}>{item.label}</button>
            }
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ═══════════════════════════════════════════════════════════
// USER SLIDE PANEL
// ═══════════════════════════════════════════════════════════
const getCabinetNav = (lang) => [
  { key: 'issued',    icon: 'fa-solid fa-book-open',         label: t(lang, 'cab_issued')    },
  { key: 'history',   icon: 'fa-solid fa-clock-rotate-left', label: t(lang, 'cab_history')   },
  { key: 'visits',    icon: 'fa-solid fa-calendar-days',     label: t(lang, 'cab_visits')    },
  { key: 'fulltexts', icon: 'fa-solid fa-file-lines',        label: t(lang, 'cab_fulltexts') },
  { key: 'bookmarks', icon: 'fa-solid fa-bookmark',          label: t(lang, 'cab_bookmarks') },
  { key: 'orders',    icon: 'fa-solid fa-cart-shopping',     label: t(lang, 'cab_orders')    },
  { key: 'queries',   icon: 'fa-solid fa-magnifying-glass',  label: t(lang, 'cab_queries')   },
];

const getGuestCabinetNav = (lang) => [
  { key: 'bookmarks', icon: 'fa-solid fa-bookmark',         label: t(lang, 'cab_bookmarks') },
  { key: 'queries',   icon: 'fa-solid fa-magnifying-glass', label: t(lang, 'cab_queries')   },
];

function UserSlidePanel({ user, lang, open, onClose, onSelectSection, userCategory, onLogout, onLoginClick, counts = {}, hasPhoto = false }) {
  const isGuest = user?.mode === 'guest';
  const nav = isGuest
  ? getGuestCabinetNav(lang)
  : getCabinetNav(lang);

  const initials = isGuest
    ? <i className="fa-solid fa-user-secret" style={{ fontSize: '1.1rem' }} />
    : avatarInitials(user?.name);

  // Фото читача (сесія 2026-10-25) — той самий спільний fetchReaderPhoto, що
  // й аватарка в кабінеті: якщо фото вже завантажене там, запиту не буде.
  const [photoSrc, setPhotoSrc] = useState(null);
  const [photoError, setPhotoError] = useState(false);
  useEffect(() => {
    if (isGuest || !hasPhoto) { setPhotoSrc(null); return; }
    let alive = true;
    fetchReaderPhoto(user).then(src => { if (alive) { setPhotoError(false); setPhotoSrc(src); } });
    return () => { alive = false; };
  }, [isGuest, hasPhoto, user]);
  const showPhoto = !!photoSrc && !photoError;

  // Блокуємо скрол сторінки, поки панель відкрита — інакше на мобіл
  // (де 100dvh теж не рятує повністю) скрол «провалюється» на body,
  // а не на саму панель (той самий прийом, що й у мобільному пошуку).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  return createPortal(
    <>
      {open && <div className="user-panel-overlay" onClick={onClose} aria-hidden="true" />}
      <aside
        className={`user-panel${open ? ' user-panel--open' : ''}`}
        aria-label={isGuest ? t(lang, 'guest_panel_title') : t(lang, 'cab_personal_title')}
        aria-hidden={!open}
      >
        <div className="user-panel-header">
          <span className="user-panel-title">
            {isGuest ? t(lang, 'guest_mode_label') : t(lang, 'cab_personal_title')}
          </span>
          <button className="user-panel-close" onClick={onClose} aria-label={t(lang, 'sidebar_close')}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="user-panel-profile">
          <div className={`user-panel-avatar${isGuest ? ' user-panel-avatar--guest' : ''}${showPhoto ? ' user-panel-avatar--photo' : ''}`}>
            {showPhoto
              ? <img src={photoSrc} alt={user?.name || ''} className="user-panel-avatar-img"
                  onError={() => setPhotoError(true)} />
              : initials}
          </div>
          <div className="user-panel-info">
            <div className="user-panel-name">
              {isGuest ? t(lang, 'cab_guest_name') : formatInitials(user?.name)}
            </div>
            <div className="user-panel-role">
              {isGuest ? t(lang, 'guest_label') : (userCategory || t(lang, 'reader_default_category'))}
            </div>
          </div>
        </div>

        {isGuest && (
          <div className="user-panel-guest-notice">
            <i className="fa-solid fa-circle-info" />
            <span>
              {t(lang, 'guest_login_prompt')}
            </span>
          </div>
        )}
        {isGuest && onLoginClick && (
          <button
            type="button"
            className="user-panel-guest-login-btn"
            onClick={() => { onClose(); onLoginClick(); }}
          >
            <i className="fa-solid fa-right-to-bracket" />{t(lang, 'login_btn')}
          </button>
        )}

        <nav className="user-panel-nav">
          {nav.map(item => (
            <button
              key={item.key}
              className="user-panel-nav-item"
              onClick={() => { onSelectSection(item.key); onClose(); }}
            >
              <i className={`${item.icon} user-panel-nav-icon`} />
              <span className="user-panel-nav-label">{item.label}</span>
              {counts[item.key] > 0 && <span className="navbar-filter-badge">{counts[item.key]}</span>}
              <i className="fa-solid fa-chevron-right user-panel-nav-chevron" />
            </button>
          ))}
        </nav>

        <div className="user-panel-footer">
          <button
            className="user-panel-logout-btn"
            onClick={() => { onClose(); onLogout && onLogout(); }}
          >
            <i className="bi bi-box-arrow-right me-2" />{t(lang, 'logout_full')}
          </button>
        </div>
      </aside>
    </>,
    document.body
  );
}


// ═══════════════════════════════════════════════════════════
// DISCIPLINE AUTOCOMPLETE
// Жива підказка по мірі введення для поля «Дисципліна» (сайдбар пошуку).
// Той самий принцип debounce+AbortController, що й у SearchQueryAutocomplete
// вище, але спрощено: без розділу «історія», без клавіатурної навігації —
// це фільтр каталогу, не основне поле пошуку. Джерело — suggest.php?field=discipline
// (довідник RZPD.MNU, а не readTerms по каталогу — детальніше в коментарі бекенду).
// ═══════════════════════════════════════════════════════════
function DisciplineAutocomplete({ value, onChange, onKeyDown, lang, placeholder, name }) {
  const [open,    setOpen]    = useState(false);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const abortRef     = useRef(null);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();

    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const url = `${API_URL}/suggest.php?field=discipline&q=${encodeURIComponent(trimmed)}&limit=8`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error('suggest error');
        const data = await res.json();
        if (!controller.signal.aborted) setItems(data.suggestions || []);
      } catch (err) {
        if (err.name !== 'AbortError') setItems([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 180);

    return () => { clearTimeout(timer); controller.abort(); };
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const select = text => { onChange(text); setOpen(false); };

  const showList = open && value.trim().length >= 2 && (loading || items.length > 0);

  return (
    <div ref={containerRef}>
      <div className="sidebar-field-input-wrap">
        <input className="form-control form-control-sm" type="text"
          name={name} autoComplete="off"
          placeholder={placeholder} value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown} />
        {value && (
          <button type="button" className="sidebar-field-clear"
            aria-label={t(lang, 'udc_clear_input')} onClick={() => { onChange(''); setOpen(false); }}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>
      {/* Потоковий блок, НЕ position:absolute — .sidebar-accordion-body має
          overflow:hidden (потрібен для анімації розкриття), абсолютний
          список підказок обрізало б знизу */}
      {showList && (
        <ul className="sidebar-disc-ac-list">
          {items.map((s, i) => (
            <li key={i} className="sidebar-disc-ac-item" onMouseDown={() => select(s.text)}>
              <i className="fa-solid fa-graduation-cap sidebar-disc-ac-icon" />
              <span className="sidebar-disc-ac-text" title={s.text}>{s.text}</span>
            </li>
          ))}
          {loading && items.length === 0 && (
            <li className="sidebar-disc-ac-loading" role="presentation">
              <span className="sidebar-disc-ac-spinner-dot" />
              <span className="sidebar-disc-ac-spinner-dot" />
              <span className="sidebar-disc-ac-spinner-dot" />
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SEARCH SIDEBAR
// ═══════════════════════════════════════════════════════════
// Бейдж-індикатор «фільтр застосовано до поточних результатів» у бічній панелі.
// count — число (для «Мова»), інакше показується галочка.
function FilterBadge({ on, count, lang }) {
  if (!on) return null;
  return (
    <span className={`sidebar-filter-badge${!count || count < 10 ? ' sidebar-filter-badge--round' : ''}`} role="img"
      aria-label={t(lang, 'sidebar_filter_applied')} title={t(lang, 'sidebar_filter_applied')}>
      {count ? count : <i className="fa-solid fa-check sidebar-filter-badge-icon" aria-hidden="true" />}
    </span>
  );
}

// Клас підпису фільтра: світло-зелена «пігулка», коли фільтр застосовано
const filterTextCls = on => `sidebar-filter-text${on ? ' sidebar-filter-text--applied' : ''}`;

function SearchSidebar({
  applied = {},
  author, setAuthor, title, setTitle, discipline, setDiscipline,
  vydType, setVydType, yearFrom, setYearFrom, yearTo, setYearTo,
  hasFulltext, setHasFulltext,
  hasExternalFulltext, setHasExternalFulltext,
  selectedLangs, setSelectedLangs,
  keywords, setKeywords, narrowVisible,
  onSearch, onReset, loading, onClose,
  onOpenUdc, lang, open,
}) {
  const [vydOpen,  setVydOpen]  = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const vydTypes = getVydTypes(lang);
  const langFilterOptions = getLangFilterOptions(lang);
  const selectedVyd  = vydTypes.find(v => v.value === vydType) || vydTypes[0];
  const vydKey = v => `vyd_${v.toLowerCase() || 'all'}`;
  const onKey = e => { if (e.key === 'Enter') onSearch(1); };

  return (
<div className={`search-sidebar${open ? ' search-sidebar--open' : ''}`}>

      <div className="search-sidebar-header">
        <span className="search-sidebar-header-title">
          <i className="fa-solid fa-filter" />{t(lang, 'sidebar_filters_title')}
        </span>
        <button className="search-sidebar-close-btn" onClick={onClose} aria-label={t(lang, 'sidebar_close')}>
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      <div className="sidebar-section sidebar-section--accordion">
        <div className="sidebar-accordion-body" id="bio-list">
            {narrowVisible && (
              <div className="narrow-results-block">
                <div className={`narrow-results-search${applied.keywords ? ' narrow-results-search--applied' : ''}`}>
                  <div className="narrow-results-input-wrap">
                    <input
                      className="narrow-results-input"
                      type="text"
                      value={keywords}
                      onChange={e => setKeywords(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') onSearch(1); }}
                      placeholder={t(lang, 'results_narrow_ph')}
                    />
                    {keywords && (
                      <button type="button" className="narrow-results-clear"
                        aria-label={t(lang, 'udc_clear_input')} onClick={() => setKeywords('')}>
                        <i className="fa-solid fa-xmark" />
                      </button>
                    )}
                  </div>
                  <button type="button" className="narrow-results-search-btn"
                    onClick={() => onSearch(1)} aria-label={t(lang, 'search_btn')}>
                    <i className="fa-solid fa-magnifying-glass" />
                  </button>
                </div>
              </div>
            )}
            {[
{ labelKey: 'sidebar_author', phKey: 'sidebar_ph_author', icon: 'fa-solid fa-user', val: author, set: setAuthor, acName: 'bio-author', on: !!applied.author },
{ labelKey: 'sidebar_title_lbl', phKey: 'sidebar_ph_title', icon: 'fa-solid fa-book', val: title, set: setTitle, acName: 'bio-title', on: !!applied.title },
            ].map(f => (
              <div key={f.labelKey} className="sidebar-field-group">
                <label className="sidebar-field-label">
                  <span className={filterTextCls(f.on)}><i className={f.icon} />{t(lang, f.labelKey)}</span>
                  <FilterBadge on={f.on} lang={lang} />
                </label>
<div className="sidebar-field-input-wrap">
  <input className="form-control form-control-sm" type="text"
    name={f.acName} autoComplete="on"
    placeholder={t(lang, f.phKey)} value={f.val}
    onChange={e => f.set(e.target.value)} onKeyDown={onKey} />
  {f.val && (
    <button type="button" className="sidebar-field-clear"
      aria-label={t(lang, 'udc_clear_input')} onClick={() => f.set('')}>
      <i className="fa-solid fa-xmark" />
    </button>
  )}
</div>
              </div>
            ))}

            <div className="sidebar-field-group sidebar-field-group--no-pad">
              <label className="sidebar-field-label">
                <span className={filterTextCls(!!applied.vydType)}><i className="fa-solid fa-bookmark" />{t(lang, 'sidebar_vyd_type')}</span>
                <FilterBadge on={!!applied.vydType} lang={lang} />
              </label>
              {/* Аккордеон вибору різновиду — стилістично аналогічний "Тип пошуку" */}
              <div className="vyd-accordion">
                <button
                  type="button"
                  className="vyd-toggle"
                  onClick={() => setVydOpen(v => !v)}
                  aria-expanded={vydOpen}
                >
                  <span className="vyd-toggle-label">{t(lang, vydKey(selectedVyd.value))}</span>
                  <i className={`fa-solid ${vydOpen ? 'fa-chevron-up' : 'fa-chevron-down'} vyd-chevron`} aria-hidden="true" />
                </button>
<div className={`sidebar-accordion-anim${vydOpen ? ' open' : ''}`}>
  <div className="sidebar-accordion-body">
    <div className="vyd-list">
      {vydTypes.map(opt => {
        const isActive = opt.value === vydType;
        return (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={isActive}
            className={`vyd-item${isActive ? ' vyd-item--active' : ''}`}
            onClick={() => { setVydType(opt.value); setVydOpen(false); }}
          >
            <span className="vyd-item-label">{t(lang, vydKey(opt.value))}</span>
            {isActive && <i className="fa-solid fa-check vyd-item-check" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  </div>
</div>
              </div>
            </div>

            <div className="sidebar-field-group">
              <label className="sidebar-field-label">
                <span className={filterTextCls(!!(applied.yearFrom || applied.yearTo))}><i className="fa-solid fa-calendar-days" />{t(lang, 'sidebar_year')}</span>
                <FilterBadge on={!!(applied.yearFrom || applied.yearTo)} lang={lang} />
              </label>
              <div className="sidebar-range-row">
                <input className="form-control form-control-sm sidebar-range-input"
                  type="number" placeholder={t(lang, 'sidebar_year_from')}
                  value={yearFrom} onChange={e => setYearFrom(e.target.value)} />
                <span className="sidebar-range-sep">—</span>
                <input className="form-control form-control-sm sidebar-range-input"
                  type="number" placeholder={t(lang, 'sidebar_year_to')}
                  value={yearTo} onChange={e => setYearTo(e.target.value)} />
              </div>
            </div>


            <div className="sidebar-checkbox form-check">
              <input type="checkbox" id="check-fulltext" className="form-check-input"
                checked={hasFulltext} onChange={e => setHasFulltext(e.target.checked)} />
              <label htmlFor="check-fulltext" className={`form-check-label ${filterTextCls(!!applied.hasFulltext)}`}>
                {t(lang, 'sidebar_fulltext_cb')}
              </label>
              <FilterBadge on={!!applied.hasFulltext} lang={lang} />
            </div>
            <div className="sidebar-checkbox form-check">
              <input type="checkbox" id="check-external-fulltext" className="form-check-input"
                checked={hasExternalFulltext} onChange={e => setHasExternalFulltext(e.target.checked)} />
              <label htmlFor="check-external-fulltext" className={`form-check-label ${filterTextCls(!!applied.hasExternalFulltext)}`}>
                {t(lang, 'has_external_fulltext')}
              </label>
              <FilterBadge on={!!applied.hasExternalFulltext} lang={lang} />
            </div>
          </div>
      </div>{/* /sidebar-section bio */}

      {/* ── Мова ── */}
      <div className="sidebar-section sidebar-section--accordion">
        <button
          className="sidebar-accordion-toggle"
          onClick={() => setLangOpen(v => !v)}
          aria-expanded={langOpen}
          aria-controls="lang-list"
        >
          <span className={filterTextCls(!!applied.langs?.length)}>
            <i className="fa-solid fa-language" aria-hidden="true" />
            <span>{t(lang, 'sidebar_lang_title')}</span>
          </span>
          <span className="sidebar-filter-badge-slot">
            <FilterBadge on={!!applied.langs?.length} count={applied.langs?.length} lang={lang} />
          </span>
          <i className={`fa-solid ${langOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
        </button>
        <div className={`sidebar-accordion-anim${langOpen ? ' open' : ''}`}>
          <div className="sidebar-accordion-body" id="lang-list">
            {langFilterOptions.map(opt => {
              const checked = selectedLangs.includes(opt.value);
              return (
                <div key={opt.value} className="sidebar-lang-row">
                  <div className="form-check flex-grow-1 mb-0">
                    <input
                      type="checkbox"
                      id={`lang-${opt.value}`}
                      className="form-check-input"
                      checked={checked}
                      onChange={e => {
                        if (e.target.checked) setSelectedLangs(v => [...v, opt.value]);
                        else setSelectedLangs(v => v.filter(l => l !== opt.value));
                      }}
                    />
                    <label htmlFor={`lang-${opt.value}`} className="form-check-label">
                      {opt.value === 'ukr' ? t(lang, 'lang_opt_ukr') : opt.label}
                    </label>
                  </div>
                  {checked && (
                    <button
                      type="button"
                      className="sidebar-lang-clear"
                      onClick={() => setSelectedLangs(v => v.filter(l => l !== opt.value))}
                      title={`${t(lang, 'sidebar_lang_deselect')} ${opt.value === 'ukr' ? t(lang, 'lang_opt_ukr') : opt.label}`}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>{/* /sidebar-section lang */}

      {/* ── Дисципліна — окремий, завжди видимий фільтр (не в акордеоні) ── */}
      <div className="sidebar-section sidebar-section--discipline">
        <div className="sidebar-field-group">
          <label className="sidebar-field-label">
            <span className={filterTextCls(!!applied.discipline)}><i className="fa-solid fa-graduation-cap" />{t(lang, 'sidebar_discipline')}</span>
            <FilterBadge on={!!applied.discipline} lang={lang} />
          </label>
          <DisciplineAutocomplete
            value={discipline} onChange={setDiscipline} onKeyDown={onKey}
            lang={lang} placeholder={t(lang, 'sidebar_ph_discipline')} name="bio-discipline" />
        </div>
      </div>

      <div className="sidebar-section">
        <button className="sidebar-udc-link" onClick={onOpenUdc}>
          <i className="fa-solid fa-sitemap" />{t(lang, 'udc_sidebar_btn')}
        </button>
      </div>

      <div className="sidebar-actions">
        <button className="btn btn-irbis-primary w-100"
          onClick={() => onSearch(1)} disabled={loading}>
          {loading
            ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, 'sidebar_finding')}</>
            : <><i className="fa-solid fa-magnifying-glass me-2" />{t(lang, 'sidebar_find')}</>}
        </button>
        <button className="sidebar-reset-btn w-100" onClick={onReset}>
          <i className="fa-solid fa-circle-xmark me-1" />{t(lang, 'sidebar_reset_all')}
        </button>
        <button className="sidebar-close-btn w-100 d-lg-none" onClick={onClose}>
          <i className="fa-solid fa-xmark me-1" />{t(lang, 'sidebar_close')}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CATALOG PAGE
// ═══════════════════════════════════════════════════════════
function CatalogPage({ user, onLogout, onLogin, onGoRegister, darkMode, onToggleDark, lang, onLangChange }) {
  useEffect(() => { trackEvent('visit', { login: readerLoginOrNull(user) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Пошук ────────────────────────────────────────────────
  const [query,       setQuery]       = useState('');
  const [db,          setDb]          = useState('KV');
  const [searchType,  setSearchType]  = useState('K_ANY');
  const [author,      setAuthor]      = useState('');
  const [title,       setTitle]       = useState('');
  const [discipline,  setDiscipline]  = useState('');
  const [keywords,    setKeywords]    = useState('');
  const [vydType,     setVydType]     = useState('');
  const [yearFrom,    setYearFrom]    = useState('');
  const [yearTo,      setYearTo]      = useState('');
  const [hasFulltext, setHasFulltext] = useState(false);
  const [hasExternalFulltext, setHasExternalFulltext] = useState(false);
  const [selectedLangs, setSelectedLangs] = useState([]);

  // ── Відображення ─────────────────────────────────────────
  const [viewMode,  setViewMode]  = useState('list');
  const [sort,      setSort]      = useState('relevance');
  const [perPage,   setPerPage]   = useState(20);

  // ── Результати ───────────────────────────────────────────
  const [results,    setResults]    = useState([]);
  const [resultsQuery, setResultsQuery] = useState(''); // текст запиту для підсвітки (сесія 2026-09-11)
  const [total,      setTotal]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [searchElapsed, setSearchElapsed] = useState(0);
  const [error,      setError]      = useState('');
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // A8: сервер ІРБІС віддає не більше ~32758 записів за один запит.
  // countCapped=true → total (чесне число) більший за retrievable (скільки
  // реально можна погортати) — показуємо підказку під лічильником.
  const [countCapped,  setCountCapped]  = useState(false);
  const [retrievable,  setRetrievable]  = useState(null);
  // relaxed=true — записів з усіма словами запиту немає, показано найближчі
  // (м'який запасний пошук search.php, сесія 2026-10-29)
  const [resultsRelaxed, setResultsRelaxed] = useState(false);
  const [searchSuggestion, setSearchSuggestion] = useState(null);
  const [correctedFrom, setCorrectedFrom] = useState(null);
  const [moreForms, setMoreForms] = useState(null);   // скільки записів додасть «інші форми слова»
  const [allFormsShown, setAllFormsShown] = useState(false);

  // ── «Вибрані» записи (сесія 2026-10-25) — SearchSelection.js ──
  const selection = useSearchSelection(user?.login);
  const isPicked  = selection.isSelected;
  const [selModalOpen, setSelModalOpen] = useState(false);
  const closeSelModal = useCallback(() => setSelModalOpen(false), []);
  const pageSelState = useMemo(() => {
    if (!results.length) return 'none';
    const n = results.filter(b => isPicked(b, db)).length;
    return n === 0 ? 'none' : (n === results.length ? 'all' : 'some');
  }, [results, isPicked, db]);
  // «Вибрати всі на сторінці» — над і під результатами (як пагінація)
  const renderPageToggle = bottom => !loading && results.length > 0 && (
    <div className={`sel-page-row${bottom ? ' sel-page-row--bottom' : ''}`}>
      <SelectPageToggle
        state={pageSelState}
        lang={lang}
        onChange={select => selection.setPage(results, db, select)}
      />
    </div>
  );

  // ── Навігація ─────────────────────────────────────────────
  const [selectedBook,   setSelectedBook]   = useState(null);
const [activeSection,  setActiveSection]  = useState(() => {
    // Посилання на підбірку (/selection/...) — для всіх, і для гостя теж
    if (parseSelectionShareUrl(window.location.pathname)) return 'selection';
    if (user?.mode === 'guest') return 'catalog';
    const stored = localStorage.getItem('irbis_active_section') || 'catalog';
    // Та сама гонка, що з журналом нижче: без адреси підбірки її розділ не відновлюємо
    if (stored === 'selection') return 'catalog';
    // Захист від гонки: якщо збережений розділ — «журнал», але поточний URL
    // не містить код журналу (напр. відкрито заново на «/», а не на
    // /journal/{db}/{code} — так стається після перезапуску npm start чи
    // відкриття нової вкладки), journalCode нижче ініціалізується порожнім,
    // і JournalPage одразу піде в journal.php?...&code= (400). Тому розділ
    // «журнал» відновлюємо з localStorage тільки якщо є відповідний URL.
    if (stored === 'journal' && !parseJournalUrl(window.location.pathname)) {
      return 'catalog';
    }
    return stored;
  });
  const [showSidebar,       setShowSidebar]       = useState(false);
  const [showMobileSearch,  setShowMobileSearch]  = useState(false);
  const [showUserPanel,     setShowUserPanel]     = useState(false);
  const [cabinetSection,    setCabinetSection]    = useState('issued');
  const [cabinetUserInfo,   setCabinetUserInfo]   = useState(null);
  const [readerActiveOrders, setReaderActiveOrders] = useState([]); // {db, mfn} — активні (необроблені) замовлення читача, для підсвітки кнопки "Замовити"
  const [userPanelCounts,   setUserPanelCounts]   = useState({ bookmarks: 0, fulltexts: 0 }); // бейджі-лічильники в панелі користувача (закладки/повні тексти)
  // Вікно входу для гостя (LoginModal.js) — замість переходу на сторінку входу.
  // «Зареєструватися» лишається повноекранною формою (onGoRegister).
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const openLoginModal  = useCallback(() => setLoginModalOpen(true), []);
  const closeLoginModal = useCallback(() => setLoginModalOpen(false), []);
  const handleGuestAuth = useCallback(view => {
    if (view === 'register') onGoRegister();
    else setLoginModalOpen(true);
  }, [onGoRegister]);

  // ── Популярні ────────────────────────────────────────────
  const [popularRecords, setPopularRecords] = useState([]);
  const [popularLoading, setPopularLoading] = useState(false);

  // ── Динамічні бази та cross-db пошук ─────────────────────
  const { crossDbDatabases } = useDatabases();
  const [cabinetReturnLabel, setCabinetReturnLabel] = useState(
    () => localStorage.getItem('irbis_active_section') === 'cabinet'
      ? t(lang, 'back_home')
      : ''
  );
  const [prevBook, setPrevBook] = useState(null);
  // Спільний стан «куди повернутися» для udc/stats/journal — раніше кожен розділ
  // мав свою окрему пару (udcPrevBook/udcReturnLabel, statsPrevBook/statsReturnLabel),
  // тепер один спільний шматок стану на всі три (аудит дублювання, сесія 2026-08-05)
  const [sectionPrevBook,    setSectionPrevBook]    = useState(null);
  const [sectionReturnLabel, setSectionReturnLabel] = useState(t(lang, 'back_home'));
  const [statsPrevSection, setStatsPrevSection] = useState('catalog');
  // Лінива ініціалізація з URL (не порожній рядок) — без цього перше
  // перезавантаження сторінки на /journal/{db}/{code} встигало б змонтувати
  // JournalPage із порожнім code ще до того, як асинхронний ефект нижче
  // встигав його підставити (activeSection відновлюється синхронно з
  // localStorage, а journalCode раніше — тільки в useEffect на монтуванні,
  // звідси гонка й запит journal.php?...&code= без значення; знайдено
  // живим тестуванням, сесія 2026-08-06)
  const [journalCode,        setJournalCode]        = useState(() => parseJournalUrl(window.location.pathname)?.code || '');
  const [journalDb,          setJournalDb]          = useState(() => parseJournalUrl(window.location.pathname)?.db || 'KV');
  const [journalInitialYear, setJournalInitialYear] = useState('');
  // Підбірка за посиланням — [{db, mfn}] з адреси (null, якщо її немає)
  const [sharedItems, setSharedItems] = useState(() => parseSelectionShareUrl(window.location.pathname));
  const [crossDbCounts,  setCrossDbCounts]  = useState([]);
  const [crossDbLoading, setCrossDbLoading] = useState(false);
  const [, startNavTransition] = useTransition();

const handlingPopState   = useRef(false);
const lastSearchQueryRef = useRef(null);

  const loadPopular = useCallback(() => {
    setPopularLoading(true);
    fetch(`${API_URL}/popular.php?action=top&limit=10`)
      .then(r => r.json())
      .then(d => { if (d.records) setPopularRecords(d.records); })
      .catch(() => {})
      .finally(() => setPopularLoading(false));
  }, []);

  useEffect(() => { loadPopular(); }, [loadPopular]);

  // Лічильник секунд під час тривалого пошуку
useEffect(() => {
  if (!loading) { setSearchElapsed(0); return; }
  const id = setInterval(() => setSearchElapsed(s => s + 1), 1000);
  return () => clearInterval(id);
}, [loading]);

  // ── Попередня загрузка даних кабінету у фоні ─────────────
  // Запускається через 1.5 сек після входу в каталог.
  // До моменту переходу в кабінет — дані вже будуть у sessionStorage.
  useEffect(() => {
    if (user?.mode === 'guest' || !user?.login) return;
    const key = `irbis_cabinet_${user.login}`;
    if (sessionStorage.getItem(key)) return; // кеш вже є — не дублюємо запит
    const timer = setTimeout(() => {
      // fetchCabinetData сам пише sessionStorage і ділить запит з кабінетом,
      // якщо читач відкриє його раніше, ніж прийде відповідь.
      fetchCabinetData(user)
        .then(d => {
          if (!d.error) setCabinetUserInfo(d); // заодно заповнюємо міні-панель
        })
        .catch(() => {});
    }, 0);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Попередня загрузка активних замовлень читача (для підсвітки кнопки
  // "Замовити" оранжевим на картці книги, якщо це замовлення — повторне) ──
  // Один раз за сесію (кешується у sessionStorage, той самий паттерн, що й
  // прев'ю кабінету вище) — action=points, який дає точну відповідь про
  // дубль, важкий (RDR + каталог + RQST), викликати його на кожне відкриття
  // картки книги для кожного читача занадто дорого для проду.
  useEffect(() => {
    if (user?.mode === 'guest' || !user?.login) return;
    const key = `irbis_active_orders_${user.login}`;
    const cached = sessionStorage.getItem(key);
    if (cached) {
      try { setReaderActiveOrders(JSON.parse(cached)); return; } catch {}
    }
    const timer = setTimeout(() => {
      fetchOrderList(user)
        .then(d => {
          if (!d.orders) return;
          const active = d.orders
            .filter(o => (o.status === 'pending' || o.status === 'reserved') && o.mfn && o.db)
            .map(o => ({ db: o.db, mfn: o.mfn }));
          try { sessionStorage.setItem(key, JSON.stringify(active)); } catch {}
          setReaderActiveOrders(active);
        })
        .catch(() => {});
    }, 0);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Відновлення стану пошуку після перезавантаження
useEffect(() => {
    // 1. Відновлення картки книги із history.state (Shift+F5)
    try {
      const hs = window.history.state;
      if (hs?.path === 'bookview' && hs?.mfn) {
        setSelectedBook({
          mfn:    hs.mfn,
          db:     hs.db     || 'KV',
          title:  hs.bookTitle || '',
          source: hs.source    || 'catalog',
        });
        if (hs.source === 'sharedSelection' && Array.isArray(hs.sharedItems) && hs.sharedItems.length) {
          setSharedItems(hs.sharedItems);
          setSection('selection');
        }
        return;
      }
    } catch {}

    // 2. Розбір URL для зовнішніх shared-посилань (/book/{db}/{mfn}-{slug})
    const bookFromUrl = parseBookUrl(window.location.pathname);
    if (bookFromUrl) {
      setSelectedBook({ mfn: bookFromUrl.mfn, db: bookFromUrl.db, title: '', source: 'catalog' });
      return;
    }

    // 3. Розбір URL для зведеного опису журналу (/journal/{db}/{code}) —
    // обробляє і shared-посилання, і звичайне перезавантаження сторінки
    // (сам рік усередині журналу відновлює JournalPage самостійно, через
    // власний sessionStorage, той самий принцип, що й у UdcPage)
    const journalFromUrl = parseJournalUrl(window.location.pathname);
    if (journalFromUrl) {
      setJournalCode(journalFromUrl.code);
      setJournalDb(journalFromUrl.db);
      setSection('journal');
      return;
    }

    // 4. Підбірка за посиланням — sharedItems/activeSection уже взяті з адреси
    // в ініціалізаторах useState; тут лише не відновлюємо поверх неї старий пошук
    if (parseSelectionShareUrl(window.location.pathname)) {
      setSection('selection');
      return;
    }

    // Пошук відновлюємо і гостю (F5, повернення з реєстрації): стан пошуку
    // в sessionStorage — лише цієї вкладки, а handleLogin його очищає при вході
    try {
      const saved = sessionStorage.getItem('irbis_search_state');
      if (saved) {
        const s = JSON.parse(saved);
        if (s.query)           setQuery(s.query);
        if (s.db)              setDb(s.db);
        if (s.results?.length) {
          setResults(s.results);
          setResultsQuery(s.query || '');
          setTotal(s.total);
          setPage(s.page);
          setTotalPages(s.totalPages);
          setCountCapped(!!s.countCapped);
          setRetrievable(s.retrievable ?? null);
          setResultsRelaxed(!!s.relaxed);
          setSearchSuggestion(s.suggestion || null);
          setCorrectedFrom(s.correctedFrom || null);
          setMoreForms(s.moreForms ?? null);
          setAllFormsShown(!!s.allForms);
          // Після F5 наступні сторінки мають іти тим самим розширеним пошуком
          if (s.allForms) lastSearchQueryRef.current = { query: s.query, db: s.db, searchType: 'K_ANY', allForms: true };
        }
      }
      const savedCrossDb = sessionStorage.getItem('irbis_search_crossdb');
      if (savedCrossDb) {
        try { setCrossDbCounts(JSON.parse(savedCrossDb)); } catch {}
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Синхронізація URL і заголовку вкладки ────────────────
useEffect(() => {
  // Если навигация произошла через кнопку «Назад/Вперёд» — не добавляем новую запись в историю
  if (handlingPopState.current) {
    handlingPopState.current = false;
    return;
  }
if (selectedBook) {
    const slug    = slugify(selectedBook.title);
    const bookUrl = `/book/${(selectedBook.db || 'kv').toLowerCase()}/${selectedBook.mfn}${slug ? '-' + slug : ''}`;
    setPageRoute('bookview', selectedBook.title || t(lang, 'book_card_title'), {
      mfn: selectedBook.mfn, db: selectedBook.db,
      bookTitle: selectedBook.title, source: selectedBook.source,
      // Книга відкрита з підбірки — список у history.state, щоб F5 і
      // «назад/вперед» повертали саме до неї (адреса тут уже /book/...)
      sharedItems: selectedBook.source === 'sharedSelection' ? sharedItems : undefined,
    }, bookUrl);
} else if (activeSection === 'cabinet') {
    const info = getCabinetPathMap(lang)[cabinetSection] || { path: 'mybooks', title: t(lang, 'cab_title') };
    setPageRoute(info.path, info.title);
  } else if (activeSection === 'journal') {
    const journalUrl = `/journal/${(journalDb || 'kv').toLowerCase()}/${encodeURIComponent(journalCode)}`;
    setPageRoute('journal', t(lang, 'journal_nav_title'), { journalCode, journalDb }, journalUrl);
  } else if (activeSection === 'selection' && sharedItems) {
    setPageRoute('selection', t(lang, 'sel_shared_title'), { sharedItems }, selectionSharePath(sharedItems));
  } else if (activeSection === 'udc') {
    setPageRoute('udcsearch', t(lang, 'udc_nav_title'));
    } else if (activeSection === 'stats') {
    setPageRoute('stats', t(lang, 'stats_short_title'));
  } else if (total !== null) {
    setPageRoute('search-results', `${t(lang, 'search_results_prefix')} (${total})`);
  } else {
    const isGuest = user?.mode === 'guest';
    setPageRoute(
      isGuest ? 'loginguest' : 'home',
      isGuest ? t(lang, 'page_title_guest_catalog') : t(lang, 'nav_home')
    );
  }
}, [selectedBook, activeSection, cabinetSection, user, total, lang, journalCode, journalDb, sharedItems]);

// обработчик кнопки «Назад/Вперёд» браузера:
useEffect(() => {
  const handlePopState = (e) => {
    handlingPopState.current = true;
    const s    = e.state || {};
    const path = s.path  || '';

    if (!path || path === 'home' || path === 'loginguest') {
      // → Головна
      setSelectedBook(null);
      setResults([]);
      setTotal(null);
      setError('');
      setSection('catalog');
      setQuery('');
      setPage(1);
      setTotalPages(1);
    } else if (path === 'search-results') {
      // → Результати пошуку (вони ще є в пам'яті)
      setSelectedBook(null);
      setSection('catalog');
    } else if (path === 'bookview' && s.mfn) {
      // → Картка книги (дані збережені в history.state)
      setSelectedBook({ mfn: s.mfn, db: s.db, title: s.bookTitle || '', source: s.source || 'catalog' });
      if (s.source === 'sharedSelection' && Array.isArray(s.sharedItems) && s.sharedItems.length) {
        setSharedItems(s.sharedItems);
        setSection('selection');
      } else {
        setSection('catalog');
      }
    } else if (path === 'udcsearch') {
      // → Навігатор УДК (знайдено 2026-08-05: раніше цієї гілки не було
      // взагалі, кнопка «назад» миші скидала на каталог замість УДК)
      setSelectedBook(null);
      setSection('udc');
    } else if (path === 'stats') {
      // → Статистика (та сама причина, що й udcsearch вище)
      setSelectedBook(null);
      setSection('stats');
    } else if (path === 'journal' && s.journalCode) {
      // → Зведений опис журналу; код/база журналу збережені в history.state
      // (setPageRoute передає їх через extra) — сам рік/номер відновлює
      // JournalPage самостійно через власний sessionStorage
      setSelectedBook(null);
      setJournalCode(s.journalCode);
      setJournalDb(s.journalDb || 'KV');
      setSection('journal');
    } else if (path === 'selection' && Array.isArray(s.sharedItems) && s.sharedItems.length) {
      // → Підбірка за посиланням (список збережений у history.state)
      setSelectedBook(null);
      setSharedItems(s.sharedItems);
      setSection('selection');
    } else {
      // → Розділ кабінету
      const found = Object.entries(getCabinetPathMap(lang)).find(([, v]) => v.path === path);
      if (found) {
        setCabinetSection(found[0]);
        setSection('cabinet');
      } else {
        setSelectedBook(null);
        setTotal(null);
        setSection('catalog');
      }
    }
  };

  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setSection = s => {
    setActiveSection(s);
    localStorage.setItem('irbis_active_section', s);
  };

  const goHome = useCallback(() => {
    try { sessionStorage.removeItem('irbis_search_state'); } catch {}
    try { sessionStorage.removeItem('irbis_search_crossdb'); } catch {}
    setSelectedBook(null);
    setResults([]);
    setTotal(null);
    setError('');
    setSection('catalog');
    setQuery('');
    setPage(1);
    setTotalPages(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenBook = useCallback((mfn, bookDb, bookTitle, source = 'catalog') => {
    setSelectedBook({ mfn, db: bookDb || db, title: bookTitle || '', source });
    if (mfn > 0 && bookDb) {
      fetch(`${API_URL}/popular.php?action=track&mfn=${mfn}&db=${encodeURIComponent(bookDb)}`)
        .catch(() => {});
      if (source === 'home') {
        fetch(`${API_URL}/popular.php?action=track_top&mfn=${mfn}&db=${encodeURIComponent(bookDb)}`)
          .catch(() => {});
      }
      const viewDetail = source === 'home' ? 'top' : source === 'journalSummary' ? 'journal_issue' : null;
         trackEvent('view', { db_name: bookDb, mfn, login: readerLoginOrNull(user), detail: viewDetail });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db]);

  const handleFulltextSave = useCallback((entry) => {
    if (!user) return;
    saveFulltextEntry(user.login || GUEST_LOGIN, entry, user);
  }, [user]);

const handleReset = () => {
    setSearchType('K_ANY'); setAuthor(''); setTitle('');
    setDiscipline(''); setKeywords(''); setVydType(''); setYearFrom(''); setYearTo('');
    setHasFulltext(false); setHasExternalFulltext(false); setSelectedLangs([]);
    // setQuery НЕ скидаємо — поисковий запит залишається при скиданні фільтрів
    // setResults і setTotal НЕ скидаємо — щоб не спрацьовував перехід на головну
    setPage(1);
    setSort('relevance'); setPerPage(20);
    setCrossDbCounts([]);
};

  const handleRepeatSearch = queryObj => {
    setQuery(queryObj.query || '');
    setAuthor(queryObj.author || '');
    setTitle(queryObj.title || '');
    setDiscipline(queryObj.discipline || '');
    setKeywords(queryObj.keywords || '');
    setYearFrom(queryObj.yearFrom || '');
    if (queryObj.db) setDb(queryObj.db);
    setSelectedBook(null);
    setSection('catalog');
    handleSearch(1, { ...queryObj, repeat: true });
  };

const handleAuthorSearch = useCallback((authorName) => {
  setQuery('');
  setAuthor(authorName);
  setSearchType('GLOBAL');
  setSelectedBook(null);
  setSection('catalog');
  setTimeout(() => {
    handleSearch(1, {
      query: '', db, searchType: 'GLOBAL',
      author: authorName, title: '', discipline: '', keywords: '', vydType: '',
      yearFrom: '', yearTo: '', hasFulltext: false,
    }, sort, perPage);
  }, 0);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [db, sort, perPage]);
  
  const handleKeywordSearch = useCallback((keyword) => {
    setQuery(keyword);
    setSearchType('K_PHRASE');
    setSelectedBook(null);
    setSection('catalog');
    setTimeout(() => {
      handleSearch(1, {
        query: keyword, db, searchType: 'K_PHRASE',
        author: '', title: '', discipline: '', keywords: '', vydType: '',
        yearFrom: '', yearTo: '', hasFulltext: false,
      }, sort, perPage);
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, sort, perPage]);

const handleUdcSearch = useCallback((udcCode) => {
    setQuery(udcCode);
    setSearchType('UDC');
    setSelectedBook(null);
    setSection('catalog');
    setTimeout(() => {
      handleSearch(1, {
        query: udcCode, db, searchType: 'UDC',
        author: '', title: '', discipline: '', keywords: '', vydType: '',
        yearFrom: '', yearTo: '', hasFulltext: false,
      }, sort, perPage);
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, sort, perPage]);

// Перехід із карточки конкретного номера періодики на сторінку зведеного опису журналу
const handleOpenJournalSummary = useCallback((code, jDb, year) => {
  setSectionPrevBook(selectedBook || null);
  setSectionReturnLabel(computeReturnLabel(lang, { selectedBook, activeSection, total }));
  setJournalCode(code);
  setJournalDb(jDb || 'KV');
  setJournalInitialYear(year || '');
  setSelectedBook(null);
  setSection('journal');
  trackEvent('journal', { db_name: jDb || 'KV', login: readerLoginOrNull(user) });
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedBook, activeSection, total, lang]);

// Перехід у конкретний розділ кабінету читача — переиспользується UserSlidePanel'ом
// і BookFullView (кнопка "Література на руках" у модалці боржника)
const handleOpenCabinetSection = useCallback((key) => {
  startNavTransition(() => {
    setPrevBook(selectedBook || null);
    setCabinetReturnLabel(
      selectedBook
        ? t(lang, 'back_book')
        : total !== null
        ? t(lang, 'back_results')
        : t(lang, 'back_home')
    );
    setCabinetSection(key);
    setSelectedBook(null);
    setSection('cabinet');
  });
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedBook, total, lang]);

// Читач щойно успішно оформив замовлення (BookFullView) — одразу додаємо
// його в кеш активних замовлень, щоб кнопка "Замовити" при повторному
// відкритті цієї ж картки (в межах тієї самої сесії) вже пофарбувалась
// оранжевим, не чекаючи наступного повного перезавантаження сторінки.
const handleOrderPlaced = useCallback((orderDb, orderMfn) => {
  setReaderActiveOrders(prev => {
    if (prev.some(o => o.db === orderDb && o.mfn === orderMfn)) return prev;
    const next = [...prev, { db: orderDb, mfn: orderMfn }];
    if (user?.login) {
      try { sessionStorage.setItem(`irbis_active_orders_${user.login}`, JSON.stringify(next)); } catch {}
    }
    return next;
  });
}, [user]);

  // Підказка при описці: сам запит не підміняємо, читач вирішує сам
  const suggestionNote = searchSuggestion && (
    <span className="search-suggestion">
      {t(lang, 'search_suggestion')}{' '}
      <button
        type="button"
        className="search-suggestion-btn"
        onClick={() => {
          setQuery(searchSuggestion);
          handleSearch(1, { ...lastSearchQueryRef.current, query: searchSuggestion, correctedFrom: null });
        }}
      >
        {searchSuggestion}
      </button>
    </span>
  );

  const handleSearch = async (pageNum = 1, overrideQuery = null, overrideSort = null, overridePerPage = null) => {
    const currentSort    = overrideSort    !== null ? overrideSort    : sort;
    const currentPerPage = overridePerPage !== null ? overridePerPage : perPage;

const q = overrideQuery || {
  query, db,
  searchType: searchType === 'UDC' ? 'K_ANY' : searchType,
  author, title, discipline, keywords,
  vydType, yearFrom, yearTo, hasFulltext, hasExternalFulltext,
  langs: selectedLangs,
};
lastSearchQueryRef.current = q;
    const hasAny = q.query?.trim() || q.author?.trim() || q.title?.trim()
      || q.discipline?.trim() || q.keywords?.trim()
      || q.vydType || q.yearFrom || q.yearTo || q.hasFulltext || q.hasExternalFulltext
      || q.langs?.length;
    if (!hasAny) return;

    setSelectedBook(null);
    setSection('catalog');
    setLoading(true);
    setError('');
    if (pageNum === 1) { setResults([]); setTotal(null); setCrossDbCounts([]); setCountCapped(false); setRetrievable(null); setResultsRelaxed(false); setSearchSuggestion(null); setCorrectedFrom(q.correctedFrom || null); setMoreForms(null); setAllFormsShown(false); }
    setShowMobileSearch(false);

if ((q.searchType || searchType) !== 'UDC') {
      saveQuery(user, {
        query: q.query || '', author: q.author || '', title: q.title || '',
        discipline: q.discipline || '', keywords: q.keywords || '',
        yearFrom: q.yearFrom || '', db: q.db || db,
        date: new Date().toLocaleDateString('uk-UA'),
      });
    }

    let searchLogType    = null;
    let searchLogDb      = null;
    let searchLogFilters = null;
    if (pageNum === 1) {
      const sType  = q.searchType || searchType || 'K_ANY';
      const dbName = q.db || db;
      searchLogDb = dbName;
      searchLogFilters = buildActiveFilters(q);
      if (sType === 'UDC') {
        searchLogType = q.seeAlso ? 'udc_see_also' : 'udc_code';
        trackEvent('udc', { search_type: searchLogType, db_name: dbName, login: readerLoginOrNull(user) });
      } else {
        const statsType = q.hasFulltext
          ? (q.query?.trim() ? 'biblio_fulltext' : 'fulltext')
          : 'bibliographic';
        searchLogType = statsType;
        // Автоматичний повтор із виправленням — той самий пошук читача, не рахуємо двічі
        if (!q.correctedFrom) {
          trackEvent('search', { search_type: statsType, db_name: dbName, login: readerLoginOrNull(user), detail: q.repeat ? 'repeat' : null });
        }
      }
    }

    const params = new URLSearchParams({
      query:       q.query      || '',
      searchType:  q.searchType || 'K_ANY',
      author:      q.author     || '',
      title:       q.title      || '',
      discipline:  q.discipline || '',
      keywords:    q.keywords   || '',
      vydType:     q.vydType    || '',
      yearFrom:    q.yearFrom   || '',
      yearTo:      q.yearTo     || '',
      hasFulltext:         q.hasFulltext         ? '1' : '0',
      hasExternalFulltext: q.hasExternalFulltext ? '1' : '0',
      langs:               (q.langs || []).join(','),
      db:          q.db         || db,
      sort:        currentSort,
      perPage:     currentPerPage,
      page:        pageNum,
    });
    if (q.allForms) params.set('allForms', '1');

 // AbortController — таймаут (більший для фільтра мови: читання великої вибірки)
    const controller = new AbortController();
    const langTimeoutMs = (q.langs && q.langs.length > 0) ? 120000 : SEARCH_TIMEOUT_MS;
    const timeoutId  = setTimeout(() => controller.abort(), langTimeoutMs);

    try {
      const res = await fetch(`${API_URL}/search.php?${params}`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await res.json();

      if (data.error) {
        setError(data.error);
      } else if (pageNum === 1 && data.total === 0 && data.suggestion && data.suggestion_sure && !q.noAutoCorrect) {
        // Упевнене виправлення при нулі: одразу показуємо видачу з поміткою. Лише один раз.
        if (searchLogType) {
          trackSearchLog(q.query, searchLogType, searchLogDb, data.total, searchLogFilters, readerLoginOrNull(user));
        }
        setQuery(data.suggestion);
        await handleSearch(1, { ...q, query: data.suggestion, correctedFrom: q.query, noAutoCorrect: true }, currentSort, currentPerPage);
        return;
      } else {
        setResults(data.results);
        setResultsQuery((q.searchType || searchType) !== 'UDC' ? (q.query || '') : '');
        setTotal(data.total);
        setPage(data.page);
        setTotalPages(data.totalPages);
        setCountCapped(!!data.count_capped);
        setRetrievable(data.retrievable ?? null);
        setResultsRelaxed(!!data.relaxed);
        if (pageNum === 1) {
          setSearchSuggestion(data.suggestion || null);
          setMoreForms(data.more_forms ?? null);
          setAllFormsShown(!!data.all_forms);
        }

        if (pageNum === 1 && searchLogType) {
          trackSearchLog(q.query, searchLogType, searchLogDb, data.total, searchLogFilters, readerLoginOrNull(user));
        }

        try {
          sessionStorage.setItem('irbis_search_state', JSON.stringify({
            query: q.query || '', db: q.db || db,
            sort: currentSort, perPage: currentPerPage,
            results: data.results, total: data.total,
            page: data.page, totalPages: data.totalPages,
            countCapped: !!data.count_capped, retrievable: data.retrievable ?? null,
            relaxed: !!data.relaxed,
            suggestion: pageNum === 1 ? (data.suggestion || null) : searchSuggestion,
            correctedFrom: pageNum === 1 ? (q.correctedFrom || null) : correctedFrom,
            moreForms: pageNum === 1 ? (data.more_forms ?? null) : moreForms,
            allForms: !!data.all_forms,
          }));
        } catch {}

        // Cross-db: паралельний пошук у вайтлісті crossDbDatabases
        if (pageNum === 1) {
          const searchDb = q.db || db;
          const otherDbs = crossDbDatabases.filter(d => d.value !== searchDb);
          if (otherDbs.length > 0) {
            setCrossDbLoading(true);
            setTimeout(() => {
              const baseParams = new URLSearchParams(params.toString());
              baseParams.set('countonly', '1');
              baseParams.set('page', '1');
              Promise.all(
                otherDbs.map(cdb => {
                  const p = new URLSearchParams(baseParams.toString());
                  p.set('db', cdb.value);
                  return fetch(`${API_URL}/search.php?${p}`)
                    .then(r => r.json())
                    .then(d => ({
                      value: cdb.value,
                      label: cdb.label,
                      count: typeof d.total === 'number' ? d.total : 0,
                    }))
                    .catch(() => ({ value: cdb.value, label: cdb.label, count: 0 }));
                })
              )
              .then(crossResults => {
                const filtered = crossResults.filter(r => r.count > 0);
                setCrossDbCounts(filtered);
                setCrossDbLoading(false);
                try { sessionStorage.setItem('irbis_search_crossdb', JSON.stringify(filtered)); } catch {}
              })
              .catch(() => setCrossDbLoading(false));
            }, 800);
          }
        }
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('__timeout__');
      } else {
        setError(t(lang, 'login_err_conn'));
      }
    } finally {
      setLoading(false);
      setShowSidebar(false);
    }
  };
  
  
  // Breadcrumbs
  const breadcrumbs = useMemo(() => {
    const crumbs = [{ label: <><i className="fa-solid fa-location-dot breadcrumb-home-icon" />{t(lang, 'nav_home')}</>, onClick: goHome }];
if (selectedBook) {
  const fromCabinet = selectedBook.source === 'cabinet';
  const fromJournal = selectedBook.source === 'journalSummary';
  const fromShared  = selectedBook.source === 'sharedSelection' && activeSection === 'selection';
  if (fromCabinet) {
    crumbs.push({ label: t(lang, 'cab_my_title'), onClick: () => setSelectedBook(null) });
  } else if (fromJournal) {
    crumbs.push({ label: t(lang, 'journal_nav_title'), onClick: () => setSelectedBook(null) });
  } else if (fromShared) {
    crumbs.push({ label: t(lang, 'sel_shared_title'), onClick: () => setSelectedBook(null) });
  } else if (total !== null) {
    crumbs.push({ label: `${t(lang, 'search_results_prefix')} (${total})`, onClick: () => setSelectedBook(null) });
  }
  crumbs.push({ label: selectedBook.title || t(lang, 'book_card_title'), current: true });
    } else if (activeSection === 'cabinet') {
      crumbs.push({ label: t(lang, 'cab_my_title'), current: true });
      } else if (activeSection === 'stats') {
      crumbs.push({ label: t(lang, 'stats_title'), current: true });
    } else if (activeSection === 'journal') {
      crumbs.push({ label: t(lang, 'journal_nav_title'), current: true });
    } else if (activeSection === 'selection') {
      crumbs.push({ label: t(lang, 'sel_shared_title'), current: true });
    } else if (activeSection === 'udc') {
      crumbs.push({ label: t(lang, 'udc_nav_title'), current: true });
    } else if (total !== null) {
      crumbs.push({ label: `${t(lang, 'search_results_prefix')} (${total})`, current: true });
    }
    return crumbs;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook, activeSection, total, goHome, lang]);

  // Слова підсвітки — похідне від ОСТАННЬОГО ВИКОНАНОГО пошуку (resultsQuery),
  // не від живого поля вводу query — інакше підсвітка "стрибала" б ще до
  // натискання Enter/лупи (сесія 2026-09-11).
  const highlightWords = useMemo(() => tokenizeHighlightWords(resultsQuery), [resultsQuery]);

const activeFilterCount = [
  selectedLangs.length > 0,
  hasFulltext,
  hasExternalFulltext,
  !!author,
  !!title,
  !!discipline,
  !!vydType,
  !!(yearFrom || yearTo),
].filter(Boolean).length;

const sidebarProps = {
    author, setAuthor, title, setTitle, discipline, setDiscipline,
    vydType, setVydType, yearFrom, setYearFrom, yearTo, setYearTo,
    hasFulltext, setHasFulltext,
    hasExternalFulltext, setHasExternalFulltext,
    selectedLangs, setSelectedLangs,
    keywords, setKeywords,
    narrowVisible: !selectedBook && activeSection === 'catalog' && total !== null,
    // Бейджі/підсвітка — у реальному часі за поточними значеннями полів панелі
    applied: buildActiveFilters({
      author, title, discipline, keywords, vydType, yearFrom, yearTo,
      hasFulltext, hasExternalFulltext, langs: selectedLangs,
    }) || {},
    onSearch: handleSearch, onReset: handleReset, loading,
    open: showSidebar,
    onClose: () => setShowSidebar(false),
onOpenUdc: () => {
  setSectionPrevBook(selectedBook || null);
  setSectionReturnLabel(computeReturnLabel(lang, { selectedBook, activeSection, total }));
  setShowSidebar(false);
  setShowMobileSearch(false);
  trackEvent('udc', { detail: 'open', db_name: db, login: readerLoginOrNull(user) });
  setSection('udc');
  setSelectedBook(null);
},
    lang,
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-body)' }} data-a11y-main>

      {/* ── Шапка ── */}
      <AppNavbar
        showHomeTitle={!selectedBook && activeSection === 'catalog' && total === null && !error}
        user={user}
        onLogout={onLogout}
        query={query}
        setQuery={setQuery}
        db={db}
        onSearch={handleSearch}
        selectedLangs={selectedLangs}
        hasFulltext={hasFulltext}
        hasExternalFulltext={hasExternalFulltext}
        searchType={searchType}
        author={author}
        title={title}
        discipline={discipline}
        keywords={keywords}
        vydType={vydType}
        yearFrom={yearFrom}
        yearTo={yearTo}
        onOpenBook={handleOpenBook}
        onGoHome={goHome}
        showMobileSearch={showMobileSearch}
        onToggleMobileSearch={() => setShowMobileSearch(v => !v)}
        onToggleUserPanel={() => {
          const next = !showUserPanel;
          setShowUserPanel(next);
          if (!next) return;
          // Спочатку пробуємо sessionStorage (prefetch міг вже заповнити його)
          if (!cabinetUserInfo && user?.login) {
            const key = `irbis_cabinet_${user.login}`;
            let cachedUsed = false;
            try {
              const cached = sessionStorage.getItem(key);
              if (cached) { setCabinetUserInfo(JSON.parse(cached)); cachedUsed = true; }
            } catch {}
            // Якщо prefetch ще не завершився — звичайний fetch
            if (!cachedUsed) {
              fetchCabinetData(user)
                .then(d => { if (!d.error) setCabinetUserInfo(d); })
                .catch(() => {});
            }
          }
          // Бейджі-лічильники «Закладки»/«Повні тексти видання» — оновлюємо
          // щоразу при відкритті панелі (легкі MySQL-запити без звернення
          // до ІРБІС, на відміну від action=points у BookFullView), щоб
          // цифра завжди відповідала реальному стану на момент відкриття.
          const isGuestUser = user?.mode === 'guest';
          const panelLogin  = isGuestUser ? GUEST_LOGIN : user?.login;
          if (panelLogin) {
            getBookmarks(panelLogin, user)
              .then(bm => setUserPanelCounts(c => ({ ...c, bookmarks: bm.length })))
              .catch(() => {});
            if (!isGuestUser) {
              getFulltexts(panelLogin, user)
                .then(fs => setUserPanelCounts(c => ({ ...c, fulltexts: new Set(fs.map(e => `${e.mfn}|${e.db}`)).size })))
                .catch(() => {});
            }
          }
        }}
        userPanelOpen={showUserPanel}
        onToggleSidebar={() => setShowSidebar(v => !v)}
        sidebarOpen={showSidebar}
        activeFilterCount={activeFilterCount}
darkMode={darkMode}
onToggleDark={onToggleDark}
lang={lang}
onLangChange={onLangChange}
      />

      {/* ── Панель користувача ── */}
      <UserSlidePanel
        user={user}
        lang={lang}
        open={showUserPanel}
        onClose={() => setShowUserPanel(false)}
        onLogout={onLogout}
        onLoginClick={openLoginModal}
        userCategory={cabinetUserInfo
          ? cabinetUserInfo.category + (cabinetUserInfo.department ? ` · ${cabinetUserInfo.department}` : '')
          : null}
          onSelectSection={handleOpenCabinetSection}
          hasPhoto={!!cabinetUserInfo?.has_photo}
          counts={{
            issued:     cabinetUserInfo?.issued?.length || 0,
            bookmarks:  userPanelCounts.bookmarks,
            fulltexts:  userPanelCounts.fulltexts,
          }}
      />

      {/* ── Вікно входу гостя ── */}
      {loginModalOpen && (
        <LoginModal
          lang={lang}
          onLogin={d => onLogin(d, true)}
          onRegister={onGoRegister}
          onClose={closeLoginModal}
        />
      )}

      {/* ── Хлібні крихти ── */}
      <Breadcrumbs items={breadcrumbs} />

{/* ── Плаваюча кнопка «Опції пошуку» — тільки мобіль/планшет (сессія 2026-08-24: повернена і для кабінету) ── */}
      {createPortal(
        <button
          className={`catalog-filter-fab d-lg-none${showSidebar ? ' active' : ''}`}
          onClick={() => setShowSidebar(v => !v)}
          aria-label={t(lang, 'sidebar_options_btn')}
          aria-expanded={showSidebar}
        >
          <i className="fa-solid fa-sliders catalog-filter-fab-icon" />
          {activeFilterCount > 0 && <span className="navbar-filter-badge">{activeFilterCount}</span>}
        </button>,
        document.body
      )}

      {/* ── Панель фільтрів пошуку — портал у document.body, щоб position:fixed
           не ламався всередині .a11y-invert-scope (filter/zoom від панелі
           доступності створюють containing block для fixed-нащадків). Один
           екземпляр на всі розділи каталогу — showSidebar/sidebarProps спільні. ── */}
      {createPortal(
        <>
          {showSidebar && (
            <div className="catalog-sidebar-overlay" onClick={() => setShowSidebar(false)} aria-hidden="true" />
          )}
          <SearchSidebar {...sidebarProps} />
        </>,
        document.body
      )}

      {/* ══ КАРТКА КНИГИ ══ */}
      {selectedBook && (
        <div className="catalog-layout">
          <div className="catalog-results-area catalog-results-area--book">
            <BookFullView
              mfn={selectedBook.mfn}
              db={selectedBook.db}
              user={user}
              lang={lang}
              trackEvent={trackEvent}
              onGoLogin={handleGuestAuth}
              backLabel={
                selectedBook.source === 'home'           ? t(lang, 'back_home')    :
                selectedBook.source === 'cabinet'        ? t(lang, 'back_cabinet') :
                selectedBook.source === 'journalSummary' ? t(lang, 'back_journal') :
                selectedBook.source === 'sharedSelection' && activeSection === 'selection' ? t(lang, 'back_selection') :
                                                    t(lang, 'back_results')
              }
              onBack={() => {
                if (selectedBook.source === 'cabinet') setSection('cabinet');
                if (selectedBook.source === 'journalSummary') setSection('journal');
                setSelectedBook(null);
              }}
              userMode={user.mode}
              onKeywordSearch={handleKeywordSearch}
              onAuthorSearch={handleAuthorSearch}
              onUdcSearch={handleUdcSearch}
              onOpenJournalSummary={handleOpenJournalSummary}
              onOpenCabinetSection={handleOpenCabinetSection}
              issuedBooks={cabinetUserInfo?.issued || []}
              activeOrders={readerActiveOrders}
              onOrderPlaced={handleOrderPlaced}
              onFulltextOpen={handleFulltextSave}
            />
          </div>
        </div>
      )}

      {/* ══ КАТАЛОГ ══ */}
      {!selectedBook && activeSection === 'catalog' && (
        <div className="catalog-layout">
          <div className="catalog-results-area">

            {/* ── Рядок метаданих і сортування ── */}
            {/* ── OOPS: таймаут відповіді сервера ── */}
            {error === '__timeout__' && (
              <div className="timeout-overlay" onClick={() => setError('')}>
                <div className="timeout-modal" onClick={e => e.stopPropagation()}>
                  <div className="timeout-modal-icon">
                    <i className="fa-solid fa-hourglass-end" />
                  </div>
                  <div className="timeout-modal-oops">OOPS!</div>
                  <p className="timeout-modal-text">
                    {t(lang, 'search_timeout_msg')}<br />
                    {t(lang, 'search_timeout_retry_hint')}
                  </p>
                  <button
                    className="timeout-modal-btn"
                    onClick={() => { setError(''); handleSearch(1); }}>
                    <i className="fa-solid fa-rotate-right me-2" />{t(lang, 'retry_btn')}
                  </button>
                  <button
                    className="timeout-modal-close"
                    onClick={() => setError('')}>
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              </div>
            )}
            {total !== null && (
              <div className="catalog-results-meta d-flex flex-wrap">
                {t(lang, 'found')}: <strong>{total}</strong>
                <span className="text-muted" style={{ fontWeight: 400, fontSize: '13px' }}>
                  ({t(lang, 'shown')} {results.length})
                </span>
                {countCapped && retrievable != null && (
                  <span className="results-relaxed-row">
                    <span className="results-relaxed-note">
                      <i className="fa-solid fa-circle-info results-relaxed-note-icon" />
                      <span className="results-relaxed-note-text">
                        {t(lang, 'results_cap_note')} ({retrievable.toLocaleString()} / {total.toLocaleString()})
                      </span>
                    </span>
                  </span>
                )}
                {correctedFrom && total > 0 && (
                  <span className="results-relaxed-row">
                    <span className="results-relaxed-note">
                      <i className="fa-solid fa-circle-info results-relaxed-note-icon" />
                      <span className="results-relaxed-note-text">
                        {t(lang, 'results_corrected_for')} <strong>{resultsQuery}</strong>.{' '}
                        {t(lang, 'results_search_instead')}{' '}
                        <button
                          type="button"
                          className="search-suggestion-btn"
                          onClick={() => {
                            setQuery(correctedFrom);
                            handleSearch(1, { ...lastSearchQueryRef.current, query: correctedFrom, correctedFrom: null, noAutoCorrect: true });
                          }}
                        >
                          {correctedFrom}
                        </button>
                      </span>
                    </span>
                  </span>
                )}
                {!correctedFrom && !resultsRelaxed && total > 0 && suggestionNote && (
                  <span className="results-relaxed-row">
                    <span className="results-relaxed-note">
                      <i className="fa-solid fa-circle-info results-relaxed-note-icon" />
                      <span className="results-relaxed-note-text">{suggestionNote}</span>
                    </span>
                  </span>
                )}
                {resultsRelaxed && total > 0 && (
                  <span className="results-relaxed-row">
                    <span className="results-relaxed-note">
                      <i className="fa-solid fa-circle-info results-relaxed-note-icon" />
                      <span className="results-relaxed-note-text">
                        {t(lang, 'results_relaxed_note')}
                        {suggestionNote && <> {suggestionNote}</>}
                      </span>
                    </span>
                  </span>
                )}
                {moreForms > 0 && (
                  <span className="results-relaxed-row">
                    <button
                      type="button"
                      className="results-more-forms-btn"
                      onClick={() => handleSearch(1, { ...lastSearchQueryRef.current, allForms: true })}
                    >
                      <i className="fa-solid fa-layer-group results-more-forms-btn-icon" />
                      <span>{t(lang, 'results_more_forms')}</span>
                      <span className="results-more-forms-btn-count">{moreForms.toLocaleString()}</span>
                    </button>
                  </span>
                )}
                {allFormsShown && (
                  <span className="results-relaxed-row">
                    <span className="results-relaxed-note">
                      <i className="fa-solid fa-circle-info results-relaxed-note-icon" />
                      <span className="results-relaxed-note-text">{t(lang, 'results_all_forms_note')}</span>
                    </span>
                  </span>
                )}

                <div className="results-controls ms-auto">
                  <div className="results-control-group">
                    <span className="results-control-label">
                      <i className="fa-solid fa-arrow-down-wide-short" />{t(lang, 'sort')}
                    </span>
                    <SortDropdown
  value={sort}
  lang={lang}
  onChange={v => { setSort(v); handleSearch(1, lastSearchQueryRef.current, v, perPage); }}
/>
                  </div>
                  <div className="results-control-group">
                    <span className="results-control-label">{t(lang, 'per_page_label')}</span>
                    <ControlDropdown
                      value={perPage}
                      options={getPerPageOptions(lang)}
                      label={t(lang, 'per_page_count_label')}
                      lang={lang}
                      onChange={v => { setPerPage(v); handleSearch(1, lastSearchQueryRef.current, sort, v); }}
                    />
                  </div>
                </div>

                <div className="view-toggle">
                  <button
                    className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title={t(lang, 'view_list')}>
                    <i className="fa-solid fa-list" />
                  </button>
                  <button
                    className={`view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title={t(lang, 'view_grid')}>
                    <i className="fa-solid fa-grip" />
                  </button>
                </div>
              </div>
            )}

            {/* ── Знайдено в інших БД ── */}
            {total !== null && (crossDbLoading || crossDbCounts.length > 0) && (
              <div className="cross-db-panel">
                <span className="cross-db-label">
                  <i className="fa-solid fa-layer-group" />{t(lang, 'found_other_db')}
                </span>
                {crossDbLoading && (
                  <Spinner animation="border" size="sm" className="ms-1 me-2" />
                )}
{crossDbCounts.map(r => (
                  <button
                    key={r.value}
                    className="cross-db-pill"
                    title={`${t(lang, 'go_to_db_search_prefix')} "${r.label}"`}
                  onClick={() => {
                  setDb(r.value);
                  const lq = lastSearchQueryRef.current;
                  handleSearch(1, lq
                  ? { ...lq, db: r.value }
                  : { query, searchType, author, title, discipline, keywords,
        vydType, yearFrom, yearTo, hasFulltext, hasExternalFulltext,
        langs: selectedLangs, db: r.value }
  );
}}
                  >
                    <span className="cross-db-pill-name">{r.label}</span>
                    <span className="cross-db-pill-count">{r.count.toLocaleString('uk-UA')}</span>
                  </button>
                ))}
              </div>
            )}


            {/* ── Верхня пагінація ── */}
            {total !== null && totalPages > 1 && !loading && (
  <PaginationBlock
    page={page}
    totalPages={totalPages}
    onGo={(p) => handleSearch(p, lastSearchQueryRef.current)}
    className="pagination-top"
    lang={lang}
  />
)}

{error && error !== '__timeout__' && (
              <Alert variant="danger" className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-exclamation-triangle-fill flex-shrink-0" />{error}
              </Alert>
            )}

{/* ── Прогрес і підказка під час тривалого пошуку ── */}
{loading && searchElapsed >= 3 && (
  <div className="search-progress-hint">
    <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
    <span>
      {selectedLangs.length > 0
        ? t(lang, 'search_hint_lang')
        : t(lang, 'search_hint_long')}
    </span>
    <span className="search-progress-timer">{searchElapsed} {t(lang, 'search_hint_sec')}</span>
  </div>
)}

            {/* Скелетони — список */}
            {loading && viewMode === 'list' && [1, 2, 3, 4, 5].map(i => (
              <Card key={i} className="mb-2 skeleton-item border-0 shadow-sm">
                <Card.Body className="p-3">
                  <div className="skeleton skeleton-title" />
                  <div className="skeleton skeleton-text w-50" />
                </Card.Body>
              </Card>
            ))}

            {/* Скелетони — сітка */}
            {loading && viewMode === 'grid' && (
              <div className="results-grid">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="result-card--grid result-card--grid-skeleton">
                    <div className="result-grid-skeleton-cover" />
                    <div className="result-card-grid-info">
                      <div className="skeleton" style={{ height: '13px', width: '85%', marginBottom: '8px' }} />
                      <div className="skeleton" style={{ height: '11px', width: '60%' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {renderPageToggle(false)}

            {/* Картки результатів */}
            {!loading && (
              <div className={viewMode === 'grid' ? 'results-grid' : 'results-list'}>
                {results.map((book, idx) => {
                  const openBook  = () => handleOpenBook(book.mfn, book.db || db, book.title, 'catalog');
                  const picked    = selection.isSelected(book, db);
                  const pickCheck = (
                    <SelectCheckbox
                      checked={picked}
                      onToggle={() => selection.toggle(book, db)}
                      label={`${t(lang, 'sel_select_item')}: ${book.title || ''}`}
                      className={viewMode === 'grid' ? 'sel-check--grid' : 'sel-check--list'}
                    />
                  );



                  if (viewMode === 'grid') {
                    return (
                      <div key={`${book.db || db}|${book.mfn}`}
                        className={`result-card result-card--grid${book.is_exact_match ? ' result-card--exact' : ''}${picked ? ' result-card--selected' : ''}`}
                        style={{ '--card-index': idx }}
                        role="button" tabIndex={0}
                        onClick={openBook}
                        onKeyDown={e => e.key === 'Enter' && openBook()}>
                        <div className="result-card-grid-cover">
                          {pickCheck}
                          {book.cover_url
                            ? <img src={book.cover_url} alt=""
                                className="result-grid-img"
                                onError={coverImgError} />
                            : null}
                          <CoverPlaceholder cls="result-grid-placeholder" hidden={!!book.cover_url}
  type={book.type} db={book.db || db} />
                          <div className="result-grid-overlay">
                            <i className="fa-regular fa-eye me-1" />{t(lang, 'view_btn')}
                          </div>
{book.has_fulltext && (
  <span className="result-fulltext-badge" title={t(lang, 'fulltext_available_title')}>
    <i className="fa-solid fa-file-lines me-1" />{t(lang, 'fulltext_available_badge')}
  </span>
)}
                        </div>
                        <div className="result-card-grid-info">
                          <div className="result-title result-title--grid">
  {highlightText(book.title, highlightWords)}
  {book.parallel_title && (
    <span className="result-title-parallel"> = {book.parallel_title}</span>
  )}
  {(book.ser_designation || book.vol_title || book.issue_num) && (
    <span className="result-title-serial">
      {book.ser_designation
        ? (' ' + book.ser_designation)
        : ((book.vol_title ? (/^\d/.test(book.vol_title) ? ' Т.\u00A0' : ' ') + book.vol_title : '') + (book.issue_num ? '. № ' + book.issue_num : ''))}
    </span>
  )}
  {book.subtitle && (
    <span className="result-title-subtitle"> : {highlightText(book.subtitle, highlightWords)}</span>
  )}
</div>
                          {(sort === 'author_asc' || sort === 'author_desc')
  ? book.authors && book.authors.length > 0
    ? <div className="result-meta-author result-meta-author--highlighted mt-1">
        <i className="fa-solid fa-user" />{highlightText(book.authors.join(' ; '), highlightWords)}
      </div>
    : <div className="result-meta-author result-meta-author--none mt-1">
        <i className="fa-solid fa-user-slash" />{t(lang, 'no_author')}
      </div>
: (() => {
      const ssCandidates = author
        ? [author.split(/[\s,.]+/)[0].toLowerCase()]
        : (searchType === 'AUTHOR' ? [query.split(/[\s,.]+/)[0].toLowerCase()] : highlightWords);
      const ai = ssCandidates.length && book.authors?.length
        ? book.authors.findIndex(a => ssCandidates.some(w => a.toLowerCase().startsWith(w)))
        : -1;
      if (ai > 0) return (
        <>
          {book.author && <div className="result-meta-author mt-1">
            <i className="fa-regular fa-user" />{highlightText(book.author, highlightWords)}
          </div>}
          <div className="result-meta-author result-contents-author--found mt-1">
            <i className="fa-solid fa-user" />{book.authors[ai]}
          </div>
        </>
      );
      return book.author
        ? <div className="result-meta-author mt-1">
            <i className="fa-regular fa-user" />{highlightText(book.author, highlightWords)}
          </div>
        : null;
    })()
}
                          <ResultMetaBadges book={book} lang={lang} mt showFulltext={false} />
                        </div>
                      </div>
                    );
                  }
                  // ── Список ──
                  return (
                    <div key={`${book.db || db}|${book.mfn}`}
                      className={`result-card${book.is_exact_match ? ' result-card--exact' : ''}${picked ? ' result-card--selected' : ''}`}
                      style={{ '--card-index': idx }}
                      role="button" tabIndex={0}
                      onClick={openBook}
                      onKeyDown={e => e.key === 'Enter' && openBook()}>
                      <div className="d-flex gap-3 align-items-start p-3">
                        {pickCheck}
                        {/* Обкладинка або заглушка */}
                        {book.cover_url
                          ? <img src={book.cover_url} alt=""
    className="result-cover flex-shrink-0"
    onError={coverImgError} />
  : null}
<CoverPlaceholder cls="result-cover-placeholder flex-shrink-0" hidden={!!book.cover_url}
  type={book.type} db={book.db || db} />

                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="result-title">
  {highlightText(book.title, highlightWords)}
  {book.parallel_title && (
    <span className="result-title-parallel"> = {book.parallel_title}</span>
  )}
  {(book.ser_designation || book.vol_title || book.issue_num) && (
    <span className="result-title-serial">
      {book.ser_designation
        ? (' ' + book.ser_designation)
        : ((book.vol_title ? (/^\d/.test(book.vol_title) ? ' Т.\u00A0' : ' ') + book.vol_title : '') + (book.issue_num ? '. № ' + book.issue_num : ''))}
    </span>
  )}
  {book.subtitle && (
    <span className="result-title-subtitle"> : {highlightText(book.subtitle, highlightWords)}</span>
  )}
</div>
                          <div className="result-meta">
{/* При сортуванні за автором — показуємо всіх авторів */}
{(() => {
  const ca = book.contents_authors || [];
  // Ищем искомого автора по фамилии (первое слово из фильтра) или по словам запроса
const surnameCandidates = author
  ? [author.split(/[\s,.]+/)[0].toLowerCase()]
  : (searchType === 'AUTHOR' ? [query.split(/[\s,.]+/)[0].toLowerCase()] : highlightWords);
  const foundIdx = surnameCandidates.length && ca.length
    ? ca.findIndex(a => surnameCandidates.some(w => a.toLowerCase().startsWith(w)))
    : -1;

  // Если есть содержание (330) — показываем авторов из него
  if (ca.length > 0) {
    const first5 = ca.slice(0, 5);
    const hasMore = ca.length > 5 || foundIdx >= 5;
const showFound = foundIdx >= 5;
const isContentsOnly = foundIdx >= 0 && surnameCandidates.length
  ? !book.authors?.some(a => surnameCandidates.some(w => a.toLowerCase().startsWith(w)))
  : false;
const authorsFallbackIdx = foundIdx === -1 && surnameCandidates.length && book.authors?.length
  ? book.authors.findIndex(a => surnameCandidates.some(w => a.toLowerCase().startsWith(w)))
  : -1;
return (
  <div className="result-contents-authors">
    <i className="fa-solid fa-users me-1" style={{opacity:0.5}} />
{first5.map((a, i) => (
  <span
    key={i}
    className={`result-contents-author${
      foundIdx === i ? ' result-contents-author--found' : ''
    }`}
  >
    {highlightText(a, highlightWords)}
    {foundIdx === i && isContentsOnly && <span className="result-ca-label">{t(lang, 'result_ca_label')}</span>}
    {i < first5.length - 1 ? ',\u00A0' : ''}
  </span>
))}
    {hasMore && <span className="result-contents-ellipsis"> {t(lang, 'and_others')}</span>}
{showFound && (
  <span className="result-contents-author--found ms-1">
    <i className="fa-solid fa-user me-1" />{ca[foundIdx]}
    {isContentsOnly && <span className="result-ca-label">{t(lang, 'result_ca_label')}</span>}
  </span>
)}
    {authorsFallbackIdx >= 0 && (
      <span className="result-contents-author--found ms-1">
        <i className="fa-solid fa-user me-1" />{book.authors[authorsFallbackIdx]}
      </span>
    )}
  </div>
);
  }

  // Обычный вывод авторов (без поля 330)
  if (sort === 'author_asc' || sort === 'author_desc') {
    return book.authors?.length > 0
      ? <span className="result-meta-author result-meta-author--highlighted">
          <i className="fa-solid fa-user" />{highlightText(book.authors.join(' ; '), highlightWords)}
        </span>
      : <span className="result-meta-author result-meta-author--none">
          <i className="fa-solid fa-user-slash" />{t(lang, 'no_author')}
        </span>;
  }
 if (surnameCandidates.length && book.authors?.length > 0) {
    const allIdx = book.authors.findIndex(a => surnameCandidates.some(w => a.toLowerCase().startsWith(w)));
    if (allIdx > 0) {
      const MAX = 4;
      const shown = book.authors.slice(0, MAX);
      const hasMore = book.authors.length > MAX;
      const isVisible = allIdx < MAX;
      return (
        <span className="result-meta-author">
          <i className="fa-regular fa-user" />
          {shown.map((a, i) => (
            <span key={i} className={allIdx === i ? 'result-contents-author--found' : ''}>
              {highlightText(a, highlightWords)}{i < shown.length - 1 ? ',\u00A0' : ''}
            </span>
          ))}
          {hasMore && <span className="result-contents-ellipsis"> {t(lang, 'and_others')}</span>}
          {!isVisible && (
            <span className="result-contents-author--found ms-1">
              <i className="fa-solid fa-user me-1" />{book.authors[allIdx]}
            </span>
          )}
        </span>
      );
    }
  }
  
return book.author
    ? <span className="result-meta-author"><i className="fa-regular fa-user" />{highlightText(book.author, highlightWords)}</span>
    : null;
})()}
                            <ResultMetaBadges book={book} lang={lang} mt={false} showFulltext />
                          </div>
                          <div className="result-hint">
                            <i className="fa-regular fa-eye" />{t(lang, 'view_details_btn')}
                          </div>
                        </div>
                        <i className="fa-solid fa-chevron-right result-chevron align-self-center flex-shrink-0 d-none d-sm-block" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Нічого не знайдено */}
            {!loading && total === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fa-solid fa-magnifying-glass display-4 d-block mb-3 opacity-25" />
                <p>{t(lang, 'nothing_found')}</p>
                {suggestionNote && <p>{suggestionNote}</p>}
              </div>
            )}

            {/* Головна сторінка (welcome) */}
            {!loading && total === null && !error && (
              <div className="catalog-welcome">
                {/* Картка ресурсів */}
                <div className="welcome-resources-card">
                  <div className="welcome-resources-header">
                    <span className="welcome-resources-title">
                      <i className="fa-solid fa-link me-2" />
                      {t(lang, 'home_resources_title')}
                    </span>
                    <div className="welcome-resources-links">
                      <span className="welcome-resources-divider" aria-hidden="true" />
                      {RESOURCE_LINKS.map(r => (
                        <a key={r.href} href={r.href} target="_blank" rel="noreferrer"
                          className="welcome-resource-item">
                          <i className={`${r.icon} welcome-resource-icon`} />
                          <span className="welcome-resource-label">{t(lang, r.labelKey)}</span>
                          <i className="fa-solid fa-arrow-up-right-from-square welcome-resource-arrow" />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Популярні записи — на всю ширину */}
                <div className="welcome-popular-card">

                      <div className="welcome-popular-header">
                        <i className="fa-solid fa-chart-line me-2" />{t(lang, 'home_popular')}
                        {popularLoading
                          ? <span className="welcome-popular-badge">...</span>
                          : <span className="welcome-popular-badge">{t(lang, 'home_top')} {popularRecords.length || 0}</span>
                        }
                      </div>
                      {popularLoading && (
                        <div className="text-center py-4 text-muted">
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t(lang, 'loading')}
                        </div>
                      )}
                      {!popularLoading && popularRecords.length === 0 && (
                        <div className="text-center py-4 text-muted small">
                          <i className="fa-solid fa-circle-info me-1" />
                          {t(lang, 'popular_empty_hint')}
                        </div>
                      )}
                      <ul className="welcome-popular-list">
                        {popularRecords.map((rec, i) => (
                          <li key={i}
                            className={`welcome-popular-item${rec.mfn > 0 ? ' welcome-popular-item--clickable' : ''}`}
                            onClick={() => rec.mfn > 0 && handleOpenBook(rec.mfn, rec.db, rec.title, 'home')}>
<div className="welcome-popular-cover-wrap">
  {rec.cover_url && (
    <img src={rec.cover_url} alt=""
      className="welcome-popular-cover"
      onError={e => {
        e.currentTarget.style.display = 'none';
        e.currentTarget.nextElementSibling.style.display = 'flex';
      }} />
  )}
  <div className="welcome-popular-placeholder cover-art-host"
       style={{ display: rec.cover_url ? 'none' : 'flex' }}>
    <CoverArt type={rec.type} db={rec.db} />
  </div>
</div>
                            <div className="welcome-popular-info">
                              <span className="welcome-popular-title">{rec.title}</span>
                              {rec.ser_label && (
                                <span className="welcome-popular-ser">{rec.ser_label}</span>
                              )}
                              <span className="welcome-popular-meta">
                                {rec.author && <span><i className="fa-regular fa-user me-1" />{rec.author}</span>}
                                {rec.year   && <span><i className="fa-regular fa-calendar me-1" />{rec.year}</span>}
                                <span className="welcome-popular-views">
                                  <i className="fa-regular fa-eye me-1" />{rec.views}
                                </span>
                              </span>
                            </div>
                            {rec.mfn > 0 && (
                              <i className="fa-solid fa-chevron-right welcome-popular-arrow" />
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
              </div>

)}
            {renderPageToggle(true)}

            {/* Нижня пагінація */}
            {total !== null && totalPages > 1 && !loading && (
  <PaginationBlock
    page={page}
    totalPages={totalPages}
    onGo={(p) => handleSearch(p, lastSearchQueryRef.current)}
    className="pagination-bottom"
    lang={lang}
  />
)}
          </div>
        </div>
      )}

{/* ══ УДК ══ */}
      {!selectedBook && activeSection === 'udc' && (
        <div className="catalog-layout">
          <div className="catalog-results-area">
            <UdcPage
              db={db}
              lang={lang}
onSearch={(code, seeAlso) => {
  setSearchType('UDC');
  setSection('catalog');
  handleSearch(1, { query: code, searchType: 'UDC', seeAlso: !!seeAlso });
}}
onKeywordSearch={(text, resultsCount, opts) => {
  trackEvent('udc', { search_type: 'udc_keyword', db_name: db, login: readerLoginOrNull(user) });
  trackSearchLog(text, 'udc_keyword', db, resultsCount, {
    logic: opts?.logic,
    stem: opts?.stem,
  }, readerLoginOrNull(user));
}}
              backLabel={sectionReturnLabel}
              onBack={() => {
                if (sectionPrevBook) {
                  setSelectedBook(sectionPrevBook);
                  setSectionPrevBook(null);
                }
                setSection('catalog');
              }}
            />
          </div>
        </div>
      )}

      {/* ══ КАБІНЕТ ══ */}
      {!selectedBook && activeSection === 'cabinet' && (
        <div className="catalog-layout">
          <div className="cabinet-layout-area">
            <CabinetPage
            user={user}
            lang={lang}
            initialSection={cabinetSection}
            onOpenBook={(mfn, bookDb, bookTitle) =>
              handleOpenBook(mfn, bookDb, bookTitle, 'cabinet')}
            onRepeatSearch={handleRepeatSearch}
            onSectionChange={setCabinetSection}
            backLabel={cabinetReturnLabel}
            trackEvent={trackEvent}
            onOrderPlaced={handleOrderPlaced}
            onBack={() => {
              if (prevBook) {
                setSelectedBook(prevBook);
                setPrevBook(null);
              }
              setSection('catalog');
            }}
            />
          </div>
        </div>
      )}

      {/* ══ СТАТИСТИКА ══ */}
      {!selectedBook && activeSection === 'stats' && (
        <div className="catalog-layout">
          <div className="catalog-results-area">
            <StatsPage
              lang={lang}
              onBack={() => {
                if (sectionPrevBook) setSelectedBook(sectionPrevBook);
                setSection(statsPrevSection);
              }}
              backLabel={sectionReturnLabel}
            />
          </div>
        </div>
      )}

      {/* ══ ЖУРНАЛ (зведений опис) ══ */}
      {!selectedBook && activeSection === 'journal' && (
        <div className="catalog-layout">
          <div className="catalog-results-area">
            <JournalPage
              db={journalDb}
              code={journalCode}
              initialYear={journalInitialYear}
              lang={lang}
              onOpenBook={(mfn, obDb, title) => handleOpenBook(mfn, obDb, title, 'journalSummary')}
              onYearOpen={(year) => trackEvent('journal', { db_name: journalDb, detail: 'year_open', year, login: readerLoginOrNull(user) })}
              backLabel={sectionReturnLabel}
              onBack={() => {
                if (sectionPrevBook) {
                  setSelectedBook(sectionPrevBook);
                  setSectionPrevBook(null);
                }
                setSection('catalog');
              }}
            />
          </div>
        </div>
      )}

      {/* ══ ПІДБІРКА ЗА ПОСИЛАННЯМ ══ */}
      {!selectedBook && activeSection === 'selection' && sharedItems && (
        <div className="catalog-layout">
          <div className="catalog-results-area">
            <SharedSelectionPage
              items={sharedItems}
              lang={lang}
              isSelected={isPicked}
              selectedCount={selection.count}
              onAddAll={records => selection.setPage(records, null, true)}
              onOpenBook={r => handleOpenBook(r.mfn, r.db, r.title, 'sharedSelection')}
              backLabel={t(lang, 'back_home')}
              onBack={goHome}
            />
          </div>
        </div>
      )}

      <ScrollToTop lang={lang} />
      {!selectedBook && activeSection === 'catalog' && (
        <SelectionBar
          count={selection.count}
          notice={selection.notice}
          canUndo={selection.canUndo}
          onClear={selection.clear}
          onUndo={selection.undo}
          lang={lang}
        >
          <button type="button" className="sel-bar-btn sel-bar-btn--primary"
            onClick={() => setSelModalOpen(true)}
            title={t(lang, 'sel_view')} aria-label={t(lang, 'sel_view')}>
            <i className="fa-solid fa-eye sel-bar-btn-icon" />
            <span className="sel-bar-btn-label">{t(lang, 'sel_view')}</span>
          </button>
        </SelectionBar>
      )}
      <SelectionModal
        open={selModalOpen}
        items={selection.items}
        onRemove={selection.remove}
        onOpenBook={it => { setSelModalOpen(false); handleOpenBook(it.mfn, it.db, it.title, 'catalog'); }}
        onClose={closeSelModal}
        lang={lang}
        login={user?.login}
        user={user}
      />
      <AppFooter
        lang={lang}
        onOpenStats={() => {
          setStatsPrevSection(activeSection);
          setSectionPrevBook(selectedBook || null);
          setSectionReturnLabel(computeReturnLabel(lang, { selectedBook, activeSection, total }));
          trackEvent('stats', { login: readerLoginOrNull(user) });
          setSection('stats');
          setSelectedBook(null);
        }}
      />
    </div>
  );
}
// ═══════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════
function App() {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('library_user') || 'null');
    } catch { return null; }
  });

  const [darkMode, setDarkMode] = useState(() =>
    localStorage.getItem('irbis_dark') === '1'
  );
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('irbis_dark', darkMode ? '1' : '0');
  }, [darkMode]);
const [lang, setLang] = useState(() =>
    localStorage.getItem('irbis_lang') || 'uk'
  );
  useEffect(() => {
    localStorage.setItem('irbis_lang', lang);
  }, [lang]);

  // Маршрут сторінки входу — але не для deep-link на карточку книги
  // (сессия 2026-07-29): якщо в URL вже є /book/{db}/{mfn} — спершу
  // пробуємо тихий гостьовий вхід, щоб не втратити посилання з Google
  // (sitemap веде саме на такі URL).
  //
  // ⚠️ Два ОКРЕМИХ ефекти, не один — знайдено вживу (React.StrictMode
  // в index.js подвоює виклик useEffect при монтуванні в dev-режимі).
  // В одному ефекті перший виклик встигав запустити fetch і виставити
  // autoGuestAttemptedRef=true, а другий (синхронний, той самий тік)
  // бачив реф вже true і одразу проваливсь у гілку setPageRoute('login'),
  // затираючи /book/... в адресному рядку ще ДО відповіді fetch —
  // CatalogPage потім монтувався вже з зіпсованим URL. guestCheckPending
  // тримає редирект-ефект від дій, поки перевірка не завершилась.
  const autoGuestAttemptedRef = useRef(false);
  // Підбірка за посиланням (/selection/...) — той самий тихий гостьовий вхід,
  // що й для картки книги: список має відкриватися в будь-кого
  const [guestCheckPending, setGuestCheckPending] = useState(
    () => !!parseBookUrl(window.location.pathname) || !!parseSelectionShareUrl(window.location.pathname)
  );

  useEffect(() => {
    if (!guestCheckPending || autoGuestAttemptedRef.current) return;
    // Реальний користувач вже авторизований (library_user у localStorage,
    // прочитано синхронно ще до першого рендеру) — тиха спроба гостя тут
    // не потрібна взагалі; без цієї перевірки кожне перезавантаження
    // сторінки картки книги (/book/{db}/{mfn}) перезаписувало б справжню
    // сесію гостьовою (знайдено 2026-08-05, живе тестування).
    if (user) { setGuestCheckPending(false); return; }
    autoGuestAttemptedRef.current = true;
    fetch(`${API_URL}/login.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guest: true }),
    })
      .then(r => r.json())
      .then(d => { if (d.success) handleLogin(d); })
      .catch(() => {})
      .finally(() => setGuestCheckPending(false));
  }, [guestCheckPending]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) return;
    if (guestCheckPending) return; // ще триває тиха спроба гостя — URL не чіпаємо
    setPageRoute('login', t(lang, 'login_page_title'));
  }, [user, lang, guestCheckPending]); // eslint-disable-line react-hooks/exhaustive-deps

  // «Зареєструватися» у гостя (вікно входу, картка книги) — форма реєстрації
  // поверх гостьової сесії, без виходу: кругла «назад» повертає в каталог
  // гостем (книга/журнал відновлюються з адреси, вона не змінюється), а
  // успішна реєстрація з автовходом іде звичайним handleLogin.
  const [guestRegister, setGuestRegister] = useState(false);

  // keepSearch — вхід гостя через вікно входу (LoginModal): його пошук у цій
  // же вкладці лишаємо, щоб після входу читач повернувся до тих самих результатів
  const handleLogin = (d, keepSearch = false) => {
    // При будь-якому вході — очищаємо гостьові дані попередньої сесії
    try { sessionStorage.removeItem(`irbis_bookmarks_${GUEST_LOGIN}`); } catch {}
    try { sessionStorage.removeItem(`irbis_queries_${GUEST_LOGIN}`);   } catch {}
    try { sessionStorage.removeItem(selectionStorageKey(GUEST_LOGIN)); } catch {}
    if (!keepSearch) {
      try { sessionStorage.removeItem('irbis_search_state');           } catch {}
      try { sessionStorage.removeItem('irbis_search_crossdb');         } catch {}
    }
    try { localStorage.removeItem('irbis_active_section');             } catch {}
    localStorage.setItem('library_user', JSON.stringify(d));
    setUser(d);
    setGuestRegister(false);
    if (d.mode === 'guest') trackEvent('session', { detail: 'guest_login' });
  };

  const handleLogout = () => {
    // Журнал виходу (сесія 2026-08-12) — fire-and-forget, тільки для
    // зареєстрованого читача (у гостя немає reader_token). logout.php
    // сам гасить токен на сервері — без цього виклику він лишався б
    // дійсним ще до READER_TOKEN_TTL (8 год) навіть після виходу.
    if (user?.mode === 'user' && user?.reader_token) {
      fetch(`${API_URL}/logout.php`, {
        method: 'POST',
        headers: { 'X-Reader-Token': user.reader_token },
      }).catch(() => {});
    } else if (user?.mode === 'guest') {
      trackEvent('session', { detail: 'guest_logout' });
    }
    // Прибираємо кешовані персональні дані читача, що виходить, — інакше
    // на спільному комп'ютері наступна людина може прочитати їх через
    // DevTools (ПІБ, фото, історія видач/відвідувань, активні замовлення).
    if (user?.login) {
      try { sessionStorage.removeItem(`irbis_cabinet_${user.login}`); } catch {}
      try { sessionStorage.removeItem(`irbis_active_orders_${user.login}`); } catch {}
    }
    try { sessionStorage.removeItem(selectionStorageKey(user?.login)); } catch {}
    localStorage.removeItem('library_user');
    setUser(null);
  };

  // Сесія читача протухла (reader_token, TTL 8 год) — readerFetch (apiAuth.js)
  // сповіщає сюди при будь-якому 401 від захищених ендпоінтів (cabinet.php,
  // bookmarks.php, order.php, fulltext.php?action=ticket). Розлогинюємо і
  // залишаємо прапорець для екрана входу, щоб показати зрозуміле повідомлення
  // замість мовчазного "Помилка" на кнопці, яку читач саме натиснув.
  const handleSessionExpired = () => {
    try { sessionStorage.setItem('irbis_session_expired', '1'); } catch {}
    handleLogout();
  };

  useEffect(() => {
    onReaderSessionExpired(handleSessionExpired);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

return (
    <>
      <div className="a11y-invert-scope">
        {guestCheckPending ? (
          <div className="app-boot-loader"><div className="app-boot-loader-spinner" /></div>
        ) : (!user || guestRegister) ? (
          <LoginPage
            initialView={guestRegister ? 'register' : 'login'}
            onCancelRegister={guestRegister ? () => setGuestRegister(false) : undefined}
            onLogin={handleLogin}
            darkMode={darkMode}
            lang={lang}
            onLangChange={setLang}
            onToggleDark={() => setDarkMode(v => !v)}
          />
        ) : (
          // key — вхід гостя через вікно входу міняє користувача без розмонтування;
          // новий key перемонтовує каталог під читачем (як після сторінки входу),
          // відкрита книга/журнал/пошук відновлюються з адреси та sessionStorage
          <CatalogPage
            key={user.mode === 'guest' ? 'guest' : `user:${user.login}`}
            user={user}
            onLogout={handleLogout}
            onLogin={handleLogin}
            onGoRegister={() => setGuestRegister(true)}
            darkMode={darkMode}
            lang={lang}
            onLangChange={setLang}
            onToggleDark={() => setDarkMode(v => !v)}
          />
        )}
      </div>
      <AccessibilityPanel />
    </>
  );
}
export default App;